/**
 * 系统设置弹窗组件
 * 包含：账户信息、修改密码、充值 三个Tab
 */
import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { fmtDateTimeCST, fmtDateCST, fmtDateTimeShortCST, parseUTC } from '../utils/datetime';
import { ConfigData, UserStats, ModelConfigItem, KnowledgeBase, KnowledgeBaseFile, FileChunksResponse, TrashItem, CompanyProfile, CompanyFinancialInfo, CompanyAttachment, EntityType } from '../types';
import { XMarkIcon, Cog6ToothIcon, EyeIcon, EyeSlashIcon, SparklesIcon, CheckCircleIcon, ExclamationCircleIcon, DocumentArrowDownIcon, TrashIcon, BookOpenIcon, PlusIcon, DocumentTextIcon, PencilSquareIcon, ListBulletIcon, PhotoIcon, ArrowUturnLeftIcon, BuildingOffice2Icon, StarIcon as StarOutlineIcon, BanknotesIcon, PaperClipIcon, ClipboardDocumentIcon, ShieldCheckIcon, FolderOpenIcon, ShareIcon, ArrowPathIcon, UserCircleIcon, ClockIcon, WrenchScrewdriverIcon } from '@heroicons/react/24/outline';
import { StarIcon as StarSolidIcon } from '@heroicons/react/24/solid';
import { useAuth } from '../hooks/useAuth';
import { authApi, modelConfigApi, getErrorMessage, documentApi, ExportTaskInfo, knowledgeApi, API_BASE_URL, systemConfigApi, projectApi, companyApi, shareApi, payApi } from '../services/api';
import { saveAs } from 'file-saver';
import ExtractedTextEditor from './ExtractedTextEditor';
import ShareDialog from './ShareDialog';
import ReferralPoster, { PosterSlogan } from './ReferralPoster';

interface SettingsDialogProps {
  config: ConfigData;
  onConfigChange: (config: ConfigData) => void;
  isOpen: boolean;
  onClose: () => void;
  readOnly?: boolean;
}

type TabKey = 'account' | 'model' | 'password' | 'recharge' | 'tasks' | 'knowledge' | 'company' | 'trash' | 'prompt';

const TABS: { key: TabKey; label: string }[] = [
  { key: 'account', label: '账户信息' },
  { key: 'model', label: '模型选择' },
  { key: 'password', label: '修改密码' },
  { key: 'recharge', label: '充值' },
  { key: 'tasks', label: '任务列表' },
  { key: 'knowledge', label: '知识库' },
  { key: 'company', label: '企业管理' },
  { key: 'trash', label: '回收站' },
  { key: 'prompt', label: '提词管理' },
];

// 充值套餐从后端 recharge_plan 表拉取（管理面板可编辑），此处不再硬编码
const IMAGE_FILE_TYPES = ['image', 'jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'];

const SettingsDialog: React.FC<SettingsDialogProps> = ({
  config,
  isOpen,
  onClose,
  readOnly = false,
}) => {
  const { user, isAdmin, updateProfile } = useAuth();
  const [activeTab, setActiveTab] = useState<TabKey>('account');
  const [stats, setStats] = useState<UserStats | null>(null);

  // 消费明细
  const [transactions, setTransactions] = useState<import('../types').PointsTransactionItem[]>([]);
  const [transactionsLoading, setTransactionsLoading] = useState(false);
  const [transTotal, setTransTotal] = useState(0);
  const [transConsumeTotal, setTransConsumeTotal] = useState(0);
  const [transRechargeTotal, setTransRechargeTotal] = useState(0);
  const [transReferralTotal, setTransReferralTotal] = useState(0);
  const [transBalance, setTransBalance] = useState(0);
  const [transPage, setTransPage] = useState(1);
  const [transPageSize] = useState(20);
  const [transStartDate, setTransStartDate] = useState('');
  const todayStr = new Date().toISOString().slice(0, 10);
  const [transEndDate, setTransEndDate] = useState(todayStr);
  const [transTypeFilter, setTransTypeFilter] = useState('');

  // 充值页子tab: "消费明细" / "项目消费"
  const [rechargeSubTab, setRechargeSubTab] = useState<'transactions' | 'projectStats'>('transactions');

  // 项目消费统计
  const [projectStats, setProjectStats] = useState<import('../types').ProjectConsumptionStat[]>([]);
  const [projectStatsLoading, setProjectStatsLoading] = useState(false);
  const [projectStatsTotalPoints, setProjectStatsTotalPoints] = useState(0);
  const [projectStatsTotalTokens, setProjectStatsTotalTokens] = useState(0);
  const [projStatsStartDate, setProjStatsStartDate] = useState('');
  const [projStatsEndDate, setProjStatsEndDate] = useState(todayStr);

  // 修改密码
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordMsg, setPasswordMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [passwordLoading, setPasswordLoading] = useState(false);

  // 编辑个人信息
  const [editDisplayName, setEditDisplayName] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [profileEditing, setProfileEditing] = useState(false);
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileMsg, setProfileMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // 推荐码
  const [referralCode, setReferralCode] = useState<string | null>(null);
  const [referralReward, setReferralReward] = useState<number>(0);
  const [referralCopied, setReferralCopied] = useState(false);
  const [showPoster, setShowPoster] = useState(false);
  const [posterSlogans, setPosterSlogans] = useState<PosterSlogan[] | null>(null);
  const [showInvitedUsers, setShowInvitedUsers] = useState(false);
  const [invitedUsers, setInvitedUsers] = useState<Array<{
    id: number;
    username: string;
    display_name: string | null;
    total_points_used: number;
    created_at: string | null;
  }> | null>(null);
  const [invitedLoading, setInvitedLoading] = useState(false);

  // 充值
  const [rechargeMsg, setRechargeMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [rechargeLoading, setRechargeLoading] = useState<string | null>(null);
  // 充值套餐（从后端 recharge_plan 拉取，管理面板可编辑）
  const [plans, setPlans] = useState<Array<{ id: number; key: string; name: string; price: number; points: number }>>([]);
  // 支付（真实充值）
  const PAY_METHODS = [
    { key: 'alipay', label: '支付宝' },
    { key: 'wechat', label: '微信支付' },
  ];
  const [payMethod, setPayMethod] = useState<'alipay' | 'wechat'>('alipay');
  const [payOrder, setPayOrder] = useState<{ order_no: string; amount: number; points: number; pay_url: string | null; qrcode_img: string | null; pay_page_url: string | null } | null>(null);
  const [payDone, setPayDone] = useState(false);
  const payPollRef = useRef<number | null>(null);

  const stopPayPolling = useCallback(() => {
    if (payPollRef.current) { window.clearInterval(payPollRef.current); payPollRef.current = null; }
  }, []);

  // 卸载时清理轮询
  useEffect(() => stopPayPolling, [stopPayPolling]);

  // 模型选择
  const [availableModels, setAvailableModels] = useState<ModelConfigItem[]>([]);
  const [selectedModelId, setSelectedModelId] = useState<number | null>(null);
  const [modelLoading, setModelLoading] = useState(false);
  const [modelMsg, setModelMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  // 系统默认模型名称（从 system_config 加载，区别于 ModelConfigItem.is_default）
  const [systemModelName, setSystemModelName] = useState<string>('');

  // 任务列表
  const [taskList, setTaskList] = useState<ExportTaskInfo[]>([]);
  const taskListPollingRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [taskMsg, setTaskMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // 知识库
  const [knowledgeBases, setKnowledgeBases] = useState<KnowledgeBase[]>([]);
  const [kbLoading, setKbLoading] = useState(false);
  const [kbMsg, setKbMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [kbSource, setKbSource] = useState<'owned' | 'shared'>('owned');
  // 新建/编辑知识库
  const [showKbCreate, setShowKbCreate] = useState(false);
  const [kbEditingId, setKbEditingId] = useState<number | null>(null);
  const [kbName, setKbName] = useState('');
  const [kbDesc, setKbDesc] = useState('');
  const [kbSaving, setKbSaving] = useState(false);
  // 选中的知识库（查看/管理文件）
  const [selectedKbId, setSelectedKbId] = useState<number | null>(null);
  const [kbFiles, setKbFiles] = useState<KnowledgeBaseFile[]>([]);
  const [kbFilesLoading, setKbFilesLoading] = useState(false);
  const [uploadingKbId, setUploadingKbId] = useState<number | null>(null);
  // 查看分块
  const [chunksData, setChunksData] = useState<FileChunksResponse | null>(null);
  const [chunksLoading, setChunksLoading] = useState(false);
  const [expandedChunkIndex, setExpandedChunkIndex] = useState<number | null>(null);
  // 图片预览
  const [imagePreviewUrl, setImagePreviewUrl] = useState<string | null>(null);
  const [expandedImageFileId, setExpandedImageFileId] = useState<number | null>(null);

  // 将后端返回的相对路径解析为可访问的绝对 URL
  const resolveImageUrl = useCallback((url: string) => {
    if (!url || url.startsWith('http')) return url;
    return API_BASE_URL + url;
  }, []);

  // 文件编辑
  const [editingFile, setEditingFile] = useState<KnowledgeBaseFile | null>(null);
  const [editContent, setEditContent] = useState('');
  const [editSaving, setEditSaving] = useState(false);

  // 回收站
  const [trashItems, setTrashItems] = useState<TrashItem[]>([]);
  const [trashLoading, setTrashLoading] = useState(false);
  const [trashMsg, setTrashMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // ---------- 企业管理 ----------
  const [companyProfiles, setCompanyProfiles] = useState<CompanyProfile[]>([]);
  const [companyLoading, setCompanyLoading] = useState(false);
  const [companyMsg, setCompanyMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [companySource, setCompanySource] = useState<'owned' | 'shared'>('owned');
  const [selectedCompanyId, setSelectedCompanyId] = useState<number | 'new' | null>(null);
  // 企业详情子标签：basic / financial / qualifications / certificates / other
  const [companySubTab, setCompanySubTab] = useState<string>('basic');
  const [companyForm, setCompanyForm] = useState({
    name: '', address: '', legal_representative: '', legal_representative_id_number: '',
    unified_social_credit_code: '', bank_name: '', bank_account: '',
    company_phone: '', fax: '', postal_code: '', notes: '',
  });
  const [companySaving, setCompanySaving] = useState(false);

  // 财务信息
  const [financialInfos, setFinancialInfos] = useState<CompanyFinancialInfo[]>([]);
  const [fiLoading, setFiLoading] = useState(false);
  const [showFiForm, setShowFiForm] = useState(false);
  const [editingFiId, setEditingFiId] = useState<number | null>(null);
  const [fiForm, setFiForm] = useState<Partial<CompanyFinancialInfo>>({});

  // 附件
  const [attachments, setAttachments] = useState<CompanyAttachment[]>([]);
  const [attLoading, setAttLoading] = useState(false);
  const [uploadingAtt, setUploadingAtt] = useState(false);
  const [attDescription, setAttDescription] = useState('');
  const [editingAttDescId, setEditingAttDescId] = useState<number | null>(null);
  const [editingAttDescValue, setEditingAttDescValue] = useState('');
  const [companyAttTextLimit, setCompanyAttTextLimit] = useState(1500);
  const [companyAttPreviewUrl, setCompanyAttPreviewUrl] = useState<string | null>(null);
  // ---- 企业附件提取文本编辑 ----
  const [editingCompanyAttText, setEditingCompanyAttText] = useState<CompanyAttachment | null>(null);
  const [editCompanyAttContent, setEditCompanyAttContent] = useState('');
  const [editCompanyAttLoading, setEditCompanyAttLoading] = useState(false);

  // 共享弹窗
  const [shareTarget, setShareTarget] = useState<{ type: EntityType; id: number; name: string; isOwner: boolean } | null>(null);

  // 转移所有权
  const [transferTarget, setTransferTarget] = useState<{ type: EntityType; id: number; name: string } | null>(null);
  const [transferUsername, setTransferUsername] = useState('');
  const [transferCancelShares, setTransferCancelShares] = useState(false);
  const [transferring, setTransferring] = useState(false);
  const [transferChecking, setTransferChecking] = useState(false);
  const [transferCheckResult, setTransferCheckResult] = useState<{ exists: boolean; display_name?: string } | null>(null);

  const resetCompanyForm = () => {
    setCompanyForm({ name: '', address: '', legal_representative: '', legal_representative_id_number: '',
      unified_social_credit_code: '', bank_name: '', bank_account: '',
      company_phone: '', fax: '', postal_code: '', notes: '' });
  };

  const loadCompanyProfiles = useCallback(async () => {
    setCompanyLoading(true);
    try {
      const res = await companyApi.list(companySource === 'shared' ? 'shared' : undefined);
      setCompanyProfiles(res.data.companies);
    } catch {
      // 静默失败
    } finally {
      setCompanyLoading(false);
    }
  }, [companySource]);

  // 前端 subTab 名称 → 后端 category 名称的映射
  const companySubTabToCategory = (subTab: string) => {
    const map: Record<string, string> = {
      qualifications: 'qualification',
      certificates: 'certificate',
      other: 'other',
    };
    return map[subTab] || subTab;
  };

  const loadFinancialInfos = async (companyId: number) => {
    setFiLoading(true);
    try {
      const res = await companyApi.listFinancialInfos(companyId);
      setFinancialInfos(res.data.financial_infos);
    } catch {
      setFinancialInfos([]);
    } finally {
      setFiLoading(false);
    }
  };

  const loadAttachments = async (companyId: number, category?: string) => {
    setAttLoading(true);
    try {
      const res = await companyApi.listAttachments(companyId, category);
      setAttachments(res.data.attachments);
    } catch {
      setAttachments([]);
    } finally {
      setAttLoading(false);
    }
  };

  const handleCompanySave = async () => {
    if (!companyForm.name.trim()) return;
    setCompanySaving(true);
    try {
      const data = {
        name: companyForm.name.trim(),
        address: companyForm.address.trim() || null,
        legal_representative: companyForm.legal_representative.trim() || null,
        legal_representative_id_number: companyForm.legal_representative_id_number.trim() || null,
        unified_social_credit_code: companyForm.unified_social_credit_code.trim() || null,
        bank_name: companyForm.bank_name.trim() || null,
        bank_account: companyForm.bank_account.trim() || null,
        company_phone: companyForm.company_phone.trim() || null,
        fax: companyForm.fax.trim() || null,
        postal_code: companyForm.postal_code.trim() || null,
        notes: companyForm.notes.trim() || null,
      };
      if (selectedCompanyId && selectedCompanyId !== 'new') {
        await companyApi.update(selectedCompanyId, data);
        setCompanyMsg({ type: 'success', text: '企业信息已更新' });
      } else {
        await companyApi.create(data);
        setCompanyMsg({ type: 'success', text: '企业信息已创建' });
      }
      setSelectedCompanyId(null);
      resetCompanyForm();
      setCompanySubTab('basic');
      await loadCompanyProfiles();
      setTimeout(() => setCompanyMsg(null), 3000);
    } catch (err) {
      setCompanyMsg({ type: 'error', text: getErrorMessage(err, '操作失败') });
      setTimeout(() => setCompanyMsg(null), 4000);
    } finally {
      setCompanySaving(false);
    }
  };

  const handleCompanySetDefault = async (companyId: number) => {
    try {
      await companyApi.update(companyId, { is_default: true });
      await loadCompanyProfiles();
      setCompanyMsg({ type: 'success', text: '已设为默认企业' });
      setTimeout(() => setCompanyMsg(null), 3000);
    } catch (err) {
      setCompanyMsg({ type: 'error', text: getErrorMessage(err, '设置失败') });
      setTimeout(() => setCompanyMsg(null), 4000);
    }
  };

  const handleCompanyDelete = async (company: CompanyProfile) => {
    if (!window.confirm(`确定要删除企业「${company.name}」吗？\n\n关联项目、财务信息、附件将全部删除，此操作不可恢复！`)) return;
    try {
      await companyApi.delete(company.id);
      setSelectedCompanyId(null);
      resetCompanyForm();
      setCompanySubTab('basic');
      await loadCompanyProfiles();
      setCompanyMsg({ type: 'success', text: '企业信息已删除' });
      setTimeout(() => setCompanyMsg(null), 3000);
    } catch (err) {
      setCompanyMsg({ type: 'error', text: getErrorMessage(err, '删除失败') });
      setTimeout(() => setCompanyMsg(null), 4000);
    }
  };

  // 财务信息操作
  const handleFiSave = async () => {
    if (!fiForm.year || typeof selectedCompanyId !== 'number') return;
    try {
      if (editingFiId) {
        await companyApi.updateFinancialInfo(selectedCompanyId, editingFiId, fiForm);
        setCompanyMsg({ type: 'success', text: '财务信息已更新' });
      } else {
        await companyApi.createFinancialInfo(selectedCompanyId, fiForm as { year: string });
        setCompanyMsg({ type: 'success', text: '财务信息已添加' });
      }
      setShowFiForm(false);
      setEditingFiId(null);
      setFiForm({});
      await loadFinancialInfos(selectedCompanyId);
      setTimeout(() => setCompanyMsg(null), 3000);
    } catch (err) {
      setCompanyMsg({ type: 'error', text: getErrorMessage(err, '操作失败') });
      setTimeout(() => setCompanyMsg(null), 4000);
    }
  };

  const handleFiDelete = async (fiId: number, year: string) => {
    if (!window.confirm(`确定删除「${year}」年度财务信息吗？`) || typeof selectedCompanyId !== 'number') return;
    try {
      await companyApi.deleteFinancialInfo(selectedCompanyId, fiId);
      await loadFinancialInfos(selectedCompanyId);
      setCompanyMsg({ type: 'success', text: '财务信息已删除' });
      setTimeout(() => setCompanyMsg(null), 3000);
    } catch (err) {
      setCompanyMsg({ type: 'error', text: getErrorMessage(err, '删除失败') });
      setTimeout(() => setCompanyMsg(null), 4000);
    }
  };

  // 附件操作
  const handleAttUpload = async (e: React.ChangeEvent<HTMLInputElement>, category: string) => {
    const files = e.target.files;
    if (!files || !files.length || typeof selectedCompanyId !== 'number') return;
    const fileList = Array.from(files);
    const desc = attDescription.trim() || undefined;
    setUploadingAtt(true);
    try {
      for (let i = 0; i < fileList.length; i++) {
        setCompanyMsg({ type: 'success', text: `正在上传 ${i + 1}/${fileList.length}...` });
        await companyApi.uploadAttachment(selectedCompanyId, fileList[i], category, desc);
      }
      setAttDescription('');
      await loadAttachments(selectedCompanyId, category);
      await loadCompanyProfiles();
      setCompanyMsg({ type: 'success', text: `成功上传 ${fileList.length} 个文件` });
      setTimeout(() => setCompanyMsg(null), 3000);
    } catch (err) {
      setCompanyMsg({ type: 'error', text: getErrorMessage(err, '上传失败') });
      setTimeout(() => setCompanyMsg(null), 4000);
    } finally {
      setUploadingAtt(false);
      e.target.value = '';
    }
  };

  const handleAttDescriptionSave = async (attId: number) => {
    if (typeof selectedCompanyId !== 'number') return;
    try {
      await companyApi.updateAttachment(selectedCompanyId, attId, { description: editingAttDescValue.trim() });
      setEditingAttDescId(null);
      await loadAttachments(selectedCompanyId, companySubTabToCategory(companySubTab));
    } catch (err) {
      setCompanyMsg({ type: 'error', text: getErrorMessage(err, '更新描述失败') });
      setTimeout(() => setCompanyMsg(null), 4000);
    }
  };

  const handleAttDelete = async (attId: number, fileName: string) => {
    if (!window.confirm(`确定删除附件「${fileName}」吗？`) || typeof selectedCompanyId !== 'number') return;
    try {
      await companyApi.deleteAttachment(selectedCompanyId, attId);
      await loadAttachments(selectedCompanyId, companySubTabToCategory(companySubTab));
      await loadCompanyProfiles();
    } catch (err) {
      setCompanyMsg({ type: 'error', text: getErrorMessage(err, '删除失败') });
      setTimeout(() => setCompanyMsg(null), 4000);
    }
  };

  // ---- 企业附件提取文本编辑 ----
  const handleCompanyAttEditText = async (att: CompanyAttachment) => {
    if (typeof selectedCompanyId !== 'number') return;
    setEditCompanyAttLoading(true);
    setEditingCompanyAttText(att);
    setEditCompanyAttContent('');
    try {
      const res = await companyApi.getAttachmentDetail(selectedCompanyId, att.id);
      setEditCompanyAttContent(res.data.extracted_text || '');
    } catch (err) {
      setCompanyMsg({ type: 'error', text: getErrorMessage(err, '获取文件内容失败') });
      setTimeout(() => setCompanyMsg(null), 4000);
      setEditingCompanyAttText(null);
    } finally {
      setEditCompanyAttLoading(false);
    }
  };

  const handleCompanyAttSaveText = async () => {
    if (!editingCompanyAttText || typeof selectedCompanyId !== 'number') return;
    try {
      await companyApi.updateAttachment(selectedCompanyId, editingCompanyAttText.id, { extracted_text: editCompanyAttContent });
      setCompanyMsg({ type: 'success', text: `「${editingCompanyAttText.file_name}」内容已保存` });
      setTimeout(() => setCompanyMsg(null), 3000);
      setEditingCompanyAttText(null);
      setEditCompanyAttContent('');
      await loadAttachments(selectedCompanyId, companySubTabToCategory(companySubTab));
    } catch (err) {
      setCompanyMsg({ type: 'error', text: getErrorMessage(err, '保存失败') });
      setTimeout(() => setCompanyMsg(null), 4000);
    }
  };

  // 账户信息 tab 激活时加载推荐码
  useEffect(() => {
    if (isOpen && activeTab === 'account') {
      authApi.getReferralCode().then((res) => {
        setReferralCode(res.data.referral_code);
        setReferralReward(res.data.referral_reward);
      }).catch(() => {});
    }
  }, [isOpen, activeTab]);

  // 企业 tab 激活时加载
  useEffect(() => {
    if (isOpen && activeTab === 'company') {
      loadCompanyProfiles();
    }
  }, [isOpen, activeTab, loadCompanyProfiles]);

  // 企业选中时加载子数据
  useEffect(() => {
    if (selectedCompanyId && typeof selectedCompanyId === 'number') {
      if (companySubTab === 'financial') {
        loadFinancialInfos(selectedCompanyId);
      } else if (['qualifications', 'certificates', 'other'].includes(companySubTab)) {
        loadAttachments(selectedCompanyId, companySubTabToCategory(companySubTab));
      }
    }
    // 切换子标签时清空描述输入和编辑状态
    setAttDescription('');
    setEditingAttDescId(null);
  }, [selectedCompanyId, companySubTab]);

  const [editLoading, setEditLoading] = useState(false);
  const [editingKbId, setEditingKbId] = useState<number | null>(null);
  // 编辑器的图库：知识库中所有图片
  const [allKbImages, setAllKbImages] = useState<Array<{
    url: string; page?: number | null; index?: number; description?: string; file_name?: string; file_id?: number;
  }>>([]);

  // ---------- 提词管理 ----------
  const PROMPT_CONTEXT_CATEGORIES = [
    {
      category: '招标文件',
      items: [
        { key: 'project_overview', label: '项目概述' },
        { key: 'tech_scoring', label: '技术评分标准' },
        { key: 'tech_requirements', label: '技术要求' },
        { key: 'biz_scoring', label: '商务评分标准' },
        { key: 'bid_requirements', label: '投标要求' },
        { key: 'commitment', label: '相关承诺函' },
        { key: 'bid_document_format', label: '投标文件格式' },
        { key: 'disqualification', label: '废标项' },
      ],
    },
    {
      category: '知识库',
      items: [
        { key: 'project_bid_info', label: '项目投标信息' },
        { key: 'company_info', label: '企业信息' },
        { key: 'knowledge_base', label: '知识库' },
      ],
    },
  ];
  const PROMPT_CONTEXT_KEYS = PROMPT_CONTEXT_CATEGORIES.flatMap(c => c.items);
  const [promptContextTech, setPromptContextTech] = useState<Set<string>>(new Set(PROMPT_CONTEXT_KEYS.map(k => k.key)));
  const [promptContextBiz, setPromptContextBiz] = useState<Set<string>>(new Set(PROMPT_CONTEXT_KEYS.map(k => k.key)));
  const [promptConfigLoading, setPromptConfigLoading] = useState(false);
  const [promptConfigMsg, setPromptConfigMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const loadPromptConfig = useCallback(async () => {
    setPromptConfigLoading(true);
    try {
      const res = await systemConfigApi.getConfig();
      if (res.data.success && res.data.config) {
        const parseContext = (raw: string | undefined, fallback: Set<string>): Set<string> => {
          if (!raw) return fallback;
          try {
            const arr: string[] = JSON.parse(raw);
            return new Set(arr);
          } catch { return fallback; }
        };
        setPromptContextTech(parseContext(res.data.config.prompt_context_tech, new Set(PROMPT_CONTEXT_KEYS.map(k => k.key))));
        setPromptContextBiz(parseContext(res.data.config.prompt_context_biz, new Set(PROMPT_CONTEXT_KEYS.map(k => k.key))));
      }
    } catch {
      // 静默失败
    } finally {
      setPromptConfigLoading(false);
    }
  }, []);

  const savePromptConfig = async () => {
    setPromptConfigLoading(true);
    setPromptConfigMsg(null);
    try {
      const techArr = JSON.stringify(Array.from(promptContextTech));
      const bizArr = JSON.stringify(Array.from(promptContextBiz));
      await systemConfigApi.setConfig({
        prompt_context_tech: techArr,
        prompt_context_biz: bizArr,
      } as any);
      setPromptConfigMsg({ type: 'success', text: '提词配置已保存' });
      setTimeout(() => setPromptConfigMsg(null), 3000);
    } catch (err) {
      setPromptConfigMsg({ type: 'error', text: getErrorMessage(err, '保存失败') });
      setTimeout(() => setPromptConfigMsg(null), 4000);
    } finally {
      setPromptConfigLoading(false);
    }
  };

  const togglePromptContext = (
    section: 'tech' | 'biz',
    key: string,
  ) => {
    const setter = section === 'tech' ? setPromptContextTech : setPromptContextBiz;
    setter(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  // 加载统计信息
  const loadStats = useCallback(async () => {
    try {
      const res = await authApi.getStats();
      setStats(res.data);
    } catch {
      // 静默失败
    }
  }, []);

  // 加载消费明细
  const loadTransactions = useCallback(async (page = 1) => {
    setTransactionsLoading(true);
    try {
      const params: Record<string, any> = { page, page_size: transPageSize };
      if (transStartDate) params.start_date = transStartDate;
      if (transEndDate) params.end_date = transEndDate;
      if (transTypeFilter) params.type_filter = transTypeFilter;
      const res = await authApi.getPointsTransactions(params);
      setTransactions(res.data.items);
      setTransTotal(res.data.total);
      setTransConsumeTotal(res.data.consume_total);
      setTransRechargeTotal(res.data.recharge_total);
      setTransReferralTotal(res.data.referral_total);
      setTransBalance(res.data.balance);
    } catch {
      setTransactions([]);
    } finally {
      setTransactionsLoading(false);
    }
  }, [transStartDate, transEndDate, transTypeFilter, transPageSize]);

  // 筛选积分流水
  const handleSearchTransactions = () => {
    setTransPage(1);
    loadTransactions(1);
  };

  const handleTransPageChange = (newPage: number) => {
    setTransPage(newPage);
    loadTransactions(newPage);
  };

  // 加载项目消费统计
  const loadProjectStats = useCallback(async () => {
    setProjectStatsLoading(true);
    try {
      const params: Record<string, any> = {};
      if (projStatsStartDate) params.start_date = projStatsStartDate;
      if (projStatsEndDate) params.end_date = projStatsEndDate;
      const res = await authApi.getProjectConsumptionStats(params);
      setProjectStats(res.data.projects);
      setProjectStatsTotalPoints(res.data.total_points);
      setProjectStatsTotalTokens(res.data.total_tokens);
    } catch {
      setProjectStats([]);
    } finally {
      setProjectStatsLoading(false);
    }
  }, [projStatsStartDate, projStatsEndDate]);

  // 筛选项目消费统计
  const handleSearchProjectStats = () => {
    loadProjectStats();
  };
  const loadModels = useCallback(async () => {
    setModelLoading(true);
    try {
      const [availRes, currentRes, sysConfigRes] = await Promise.all([
        modelConfigApi.getAvailable(),
        modelConfigApi.getCurrent(),
        systemConfigApi.getConfig(),
      ]);
      setAvailableModels(availRes.data.configs);
      setSelectedModelId(currentRes.data.model_config_id);
      // 加载系统默认模型名称（系统配置中的 model）
      if (sysConfigRes.data.success && sysConfigRes.data.config?.model) {
        setSystemModelName(sysConfigRes.data.config.model);
      }
      // 加载企业附件文本截断限制
      if (sysConfigRes.data.success && sysConfigRes.data.config?.company_att_text_limit) {
        const limit = parseInt(sysConfigRes.data.config.company_att_text_limit, 10);
        if (!isNaN(limit) && limit > 0) setCompanyAttTextLimit(limit);
      }
    } catch {
      // 静默失败
    } finally {
      setModelLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      setActiveTab('account');
      setPasswordMsg(null);
      setRechargeMsg(null);
      setModelMsg(null);
      setOldPassword('');
      setNewPassword('');
      setConfirmPassword('');
      loadStats();
      loadModels();
    }
  }, [isOpen, loadStats, loadModels]);

  // 按 Escape 关闭
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // 进入充值 Tab 时自动加载积分流水和项目消费统计
  useEffect(() => {
    if (activeTab === 'recharge') {
      setTransPage(1);
      loadTransactions(1);
      payApi.plans().then((res) => setPlans(res.data.plans)).catch(() => {});
      loadProjectStats();
    }
  }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChangePassword = async () => {
    setPasswordMsg(null);
    if (!oldPassword) {
      setPasswordMsg({ type: 'error', text: '请输入原密码' });
      return;
    }
    if (!newPassword || newPassword.length < 6) {
      setPasswordMsg({ type: 'error', text: '新密码至少6位' });
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordMsg({ type: 'error', text: '两次输入的新密码不一致' });
      return;
    }
    setPasswordLoading(true);
    try {
      await authApi.changePassword(oldPassword, newPassword);
      setPasswordMsg({ type: 'success', text: '密码修改成功' });
      setOldPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      setPasswordMsg({ type: 'error', text: getErrorMessage(err, '修改失败') });
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleSelectModel = async (modelId: number | null) => {
    setModelMsg(null);
    try {
      const res = await modelConfigApi.select(modelId);
      setSelectedModelId(modelId);
      setModelMsg({ type: 'success', text: res.data.message });
      setTimeout(() => setModelMsg(null), 3000);
    } catch (err) {
      setModelMsg({ type: 'error', text: getErrorMessage(err, '切换模型失败') });
      setTimeout(() => setModelMsg(null), 4000);
    }
  };

  const startPayPolling = (orderNo: string) => {
    stopPayPolling();
    payPollRef.current = window.setInterval(async () => {
      try {
        const r = await payApi.status(orderNo);
        if (r.data.status === 'paid') {
          stopPayPolling();
          setPayDone(true);
          setRechargeMsg({ type: 'success', text: '充值成功，积分已到账！' });
          loadStats();
          setTimeout(() => { setPayOrder(null); setPayDone(false); }, 3000);
        } else if (r.data.status === 'cancelled' || r.data.status === 'failed') {
          stopPayPolling();
          setPayOrder(null);
          setRechargeMsg({ type: 'error', text: '订单未支付，已关闭' });
        }
      } catch { /* 忽略单次轮询失败 */ }
    }, 3000);
  };

  const handleRecharge = async (planKey: string) => {
    setRechargeMsg(null);
    setRechargeLoading(planKey);
    try {
      const res = await payApi.order(planKey, payMethod);
      setPayOrder({
        order_no: res.data.order_no,
        amount: res.data.amount,
        points: res.data.points,
        pay_url: res.data.pay_url,
        qrcode_img: res.data.qrcode_img,
        pay_page_url: res.data.pay_page_url,
      });
      setRechargeMsg({ type: 'success', text: '订单已创建，请完成支付（到账自动检测）' });
      // 支付宝返回网关 pay_url（直接跳转）；微信返回二维码 qrcode_img（下方展示扫码，不跳转）
      if (res.data.pay_url) window.open(res.data.pay_url, '_blank');
      startPayPolling(res.data.order_no);
    } catch (err) {
      setRechargeMsg({ type: 'error', text: getErrorMessage(err, '创建订单失败') });
    } finally {
      setRechargeLoading(null);
    }
  };

  const handleCancelPay = async () => {
    if (!payOrder) return;
    try { await payApi.cancel(payOrder.order_no); } catch { /* 忽略 */ }
    stopPayPolling();
    setPayOrder(null);
    setRechargeMsg({ type: 'error', text: '已取消支付' });
  };

  // 任务列表相关
  const fetchTaskList = useCallback(async () => {
    try {
      const res = await documentApi.listExportTasks();
      setTaskList(res.data.tasks);
    } catch (err) {
      console.warn('获取任务列表失败:', err);
    }
  }, []);

  const handleTaskDownload = async (task: ExportTaskInfo) => {
    try {
      const downloadUrl = documentApi.getExportDownloadUrl(task.task_id);
      const token = localStorage.getItem('auth_token');
      const downloadResponse = await fetch(downloadUrl, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
      });
      if (downloadResponse.ok) {
        const blob = await downloadResponse.blob();
        saveAs(blob, task.filename || '标书文档.docx');
        setTaskMsg({ type: 'success', text: `"${task.filename}" 下载成功` });
        fetchTaskList();
      } else {
        setTaskMsg({ type: 'error', text: '导出文件下载失败，请重试' });
      }
    } catch {
      setTaskMsg({ type: 'error', text: '下载失败，请重试' });
    }
  };

  const handleTaskDelete = async (taskId: number) => {
    try {
      await documentApi.deleteExportTask(taskId);
      setTaskList(prev => prev.filter(t => t.task_id !== taskId));
    } catch (err) {
      console.warn('删除任务失败:', err);
    }
  };

  const formatTaskTime = (isoString: string | null): string => fmtDateTimeCST(isoString);

  // 任务列表轮询
  useEffect(() => {
    if (isOpen && activeTab === 'tasks') {
      fetchTaskList();
      taskListPollingRef.current = setInterval(fetchTaskList, 3000);
    }
    return () => {
      if (taskListPollingRef.current) {
        clearInterval(taskListPollingRef.current);
        taskListPollingRef.current = null;
      }
    };
  }, [isOpen, activeTab, fetchTaskList]);

  // ---------- 知识库 ----------

  const loadKnowledgeBases = useCallback(async () => {
    setKbLoading(true);
    try {
      const res = await knowledgeApi.list(kbSource === 'shared' ? 'shared' : undefined);
      setKnowledgeBases(res.data.knowledge_bases);
    } catch {
      // 静默失败
    } finally {
      setKbLoading(false);
    }
  }, [kbSource]);

  const loadKbFiles = async (kbId: number) => {
    setKbFilesLoading(true);
    try {
      const res = await knowledgeApi.listFiles(kbId);
      setKbFiles(res.data.files);
      // 如果当前查看的分块文件已不在文件列表中，清除分块数据
      if (chunksData && !res.data.files.some((f: any) => f.id === chunksData.file_id)) {
        setChunksData(null);
        setExpandedChunkIndex(null);
      }
    } catch {
      setKbFiles([]);
    } finally {
      setKbFilesLoading(false);
    }
  };

  const handleSelectKb = (kbId: number) => {
    if (selectedKbId === kbId) {
      setSelectedKbId(null);
      setKbFiles([]);
    } else {
      setSelectedKbId(kbId);
      loadKbFiles(kbId);
    }
  };

  const handleKbCreate = async () => {
    if (!kbName.trim()) return;
    try {
      setKbSaving(true);
      if (kbEditingId) {
        await knowledgeApi.update(kbEditingId, {
          name: kbName.trim(),
          description: kbDesc.trim() || undefined,
        });
        setKbMsg({ type: 'success', text: '知识库已更新' });
      } else {
        await knowledgeApi.create({
          name: kbName.trim(),
          description: kbDesc.trim() || undefined,
        });
        setKbMsg({ type: 'success', text: '知识库已创建' });
      }
      setShowKbCreate(false);
      setKbEditingId(null);
      setKbName('');
      setKbDesc('');
      await loadKnowledgeBases();
      setTimeout(() => setKbMsg(null), 3000);
    } catch (err) {
      setKbMsg({ type: 'error', text: getErrorMessage(err, '操作失败') });
      setTimeout(() => setKbMsg(null), 4000);
    } finally {
      setKbSaving(false);
    }
  };

  const handleKbDelete = async (kb: KnowledgeBase) => {
    if (!window.confirm(`确定要删除知识库「${kb.name}」吗？其中的所有文件也将被删除。`)) return;
    try {
      await knowledgeApi.delete(kb.id);
      if (selectedKbId === kb.id) {
        setSelectedKbId(null);
        setKbFiles([]);
      }
      await loadKnowledgeBases();
      setKbMsg({ type: 'success', text: '知识库已删除' });
      setTimeout(() => setKbMsg(null), 3000);
    } catch (err) {
      setKbMsg({ type: 'error', text: getErrorMessage(err, '删除失败') });
      setTimeout(() => setKbMsg(null), 4000);
    }
  };

  const closeTransferDialog = () => {
    setTransferTarget(null);
    setTransferUsername('');
    setTransferCancelShares(false);
    setTransferCheckResult(null);
  };

  const handleCheckUser = async () => {
    const username = transferUsername.trim();
    if (!username) return;
    setTransferChecking(true);
    setTransferCheckResult(null);
    try {
      const res = await shareApi.checkUser(username);
      setTransferCheckResult(res.data);
    } catch {
      setTransferCheckResult({ exists: false });
    } finally {
      setTransferChecking(false);
    }
  };

  const handleTransfer = async () => {
    if (!transferTarget || !transferUsername.trim()) return;
    if (!transferCheckResult?.exists) {
      const msg = '请先检查目标用户名是否存在';
      if (transferTarget.type === 'knowledge_base') {
        setKbMsg({ type: 'error', text: msg });
        setTimeout(() => setKbMsg(null), 3000);
      } else {
        setCompanyMsg({ type: 'error', text: msg });
        setTimeout(() => setCompanyMsg(null), 3000);
      }
      return;
    }
    try {
      setTransferring(true);
      const res = await shareApi.transferOwnership({
        entity_type: transferTarget.type,
        entity_id: transferTarget.id,
        target_username: transferUsername.trim(),
        cancel_shares: transferCancelShares,
      });
      closeTransferDialog();
      // 根据类型重新加载列表
      if (transferTarget.type === 'knowledge_base') {
        await loadKnowledgeBases();
        setKbMsg({ type: 'success', text: res.data.message });
        setTimeout(() => setKbMsg(null), 4000);
      } else if (transferTarget.type === 'company_profile') {
        await loadCompanyProfiles();
        setCompanyMsg({ type: 'success', text: res.data.message });
        setTimeout(() => setCompanyMsg(null), 4000);
      }
    } catch (err) {
      const msg = getErrorMessage(err, '转移失败');
      if (transferTarget.type === 'knowledge_base') {
        setKbMsg({ type: 'error', text: msg });
        setTimeout(() => setKbMsg(null), 4000);
      } else {
        setCompanyMsg({ type: 'error', text: msg });
        setTimeout(() => setCompanyMsg(null), 4000);
      }
    } finally {
      setTransferring(false);
    }
  };

  const handleKbFileUpload = async (kbId: number, e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    const fileList = Array.from(files);
    try {
      setUploadingKbId(kbId);
      setKbMsg({ type: 'success', text: `正在上传 ${fileList.length} 个文件...` });
      await knowledgeApi.uploadFiles(kbId, fileList);
      setKbMsg({ type: 'success', text: `成功上传 ${fileList.length} 个文件` });
      setTimeout(() => setKbMsg(null), 3000);
      await loadKnowledgeBases();
      if (selectedKbId === kbId) {
        await loadKbFiles(kbId);
      }
    } catch (err) {
      setKbMsg({ type: 'error', text: getErrorMessage(err, '上传失败') });
      setTimeout(() => setKbMsg(null), 4000);
    } finally {
      setUploadingKbId(null);
      e.target.value = '';
    }
  };

  const handleKbFileDelete = async (kbId: number, fileId: number, fileName: string) => {
    if (!window.confirm(`确定要删除文件「${fileName}」吗？`)) return;
    try {
      await knowledgeApi.deleteFile(kbId, fileId);
      setKbMsg({ type: 'success', text: `「${fileName}」已删除` });
      setTimeout(() => setKbMsg(null), 3000);
      // 如果删除的是当前正在查看分块的文件，清除分块数据
      if (chunksData?.file_id === fileId) {
        setChunksData(null);
        setExpandedChunkIndex(null);
      }
      await loadKnowledgeBases();
      await loadKbFiles(kbId);
    } catch (err) {
      setKbMsg({ type: 'error', text: getErrorMessage(err, '删除失败') });
      setTimeout(() => setKbMsg(null), 4000);
    }
  };

  const handleViewChunks = async (kbId: number, fileId: number) => {
    setChunksLoading(true);
    setChunksData(null);
    setExpandedChunkIndex(null);
    try {
      const res = await knowledgeApi.getFileChunks(kbId, fileId);
      setChunksData(res.data);
    } catch (err) {
      setKbMsg({ type: 'error', text: getErrorMessage(err, '获取分块失败') });
      setTimeout(() => setKbMsg(null), 4000);
    } finally {
      setChunksLoading(false);
    }
  };

  // 打开文件编辑器
  const handleEditFile = async (kbId: number, file: KnowledgeBaseFile) => {
    setEditLoading(true);
    setEditingKbId(kbId);
    setEditingFile(file);
    setEditContent('');
    setAllKbImages([]);
    try {
      const [fileRes, imagesRes] = await Promise.all([
        knowledgeApi.getFile(kbId, file.id),
        knowledgeApi.listImages(kbId).catch(() => ({ data: { images: [] } })),
      ]);
      setEditContent(fileRes.data.extracted_text || '');
      setAllKbImages(imagesRes.data.images || []);
    } catch (err) {
      setKbMsg({ type: 'error', text: getErrorMessage(err, '获取文件内容失败') });
      setTimeout(() => setKbMsg(null), 4000);
      setEditingFile(null);
      setEditingKbId(null);
    } finally {
      setEditLoading(false);
    }
  };

  // 保存编辑内容
  const handleSaveEdit = async () => {
    if (!editingFile || editingKbId === null) return;
    setEditSaving(true);
    try {
      await knowledgeApi.updateFile(editingKbId, editingFile.id, editContent);
      setKbMsg({ type: 'success', text: `「${editingFile.original_name}」已保存` });
      setTimeout(() => setKbMsg(null), 3000);
      setEditingFile(null);
      setEditingKbId(null);
      setEditContent('');
      setAllKbImages([]);
    } catch (err) {
      setKbMsg({ type: 'error', text: getErrorMessage(err, '保存失败') });
      setTimeout(() => setKbMsg(null), 4000);
    } finally {
      setEditSaving(false);
    }
  };

  // 编辑器上传图片到知识库
  const handleKbImageUpload = useCallback(async (file: File): Promise<string> => {
    if (editingKbId === null) throw new Error('知识库ID无效');
    const res = await knowledgeApi.uploadImage(editingKbId, file);
    const uploadedUrl = res.data.url;
    // 刷新图库
    try {
      const imagesRes = await knowledgeApi.listImages(editingKbId);
      setAllKbImages(imagesRes.data.images || []);
    } catch {
      // 静默失败
    }
    return uploadedUrl;
  }, [editingKbId]);

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1048576).toFixed(1)} MB`;
  };

  // 知识库 tab 激活时加载列表
  useEffect(() => {
    if (isOpen && activeTab === 'knowledge') {
      loadKnowledgeBases();
    }
  }, [isOpen, activeTab, loadKnowledgeBases]);

  // ---------- 回收站 ----------

  const loadTrash = useCallback(async () => {
    setTrashLoading(true);
    try {
      const res = await projectApi.listTrash();
      setTrashItems(res.data.trash_items);
    } catch {
      setTrashItems([]);
    } finally {
      setTrashLoading(false);
    }
  }, []);

  const handleRestore = async (projectId: number) => {
    if (!window.confirm('确定要恢复该项目吗？')) return;
    try {
      await projectApi.restore(projectId);
      setTrashMsg({ type: 'success', text: '项目已恢复' });
      setTimeout(() => setTrashMsg(null), 3000);
      await loadTrash();
    } catch (error) {
      setTrashMsg({ type: 'error', text: getErrorMessage(error, '恢复失败') });
      setTimeout(() => setTrashMsg(null), 3000);
    }
  };

  const handlePermanentDelete = async (projectId: number, projectName: string) => {
    if (!window.confirm(`确定要彻底删除项目「${projectName}」吗？\n\n此操作不可撤销，所有关联数据将被永久删除。`)) return;
    try {
      await projectApi.permanentDelete(projectId);
      setTrashMsg({ type: 'success', text: '项目已彻底删除' });
      setTimeout(() => setTrashMsg(null), 3000);
      await loadTrash();
    } catch (error) {
      setTrashMsg({ type: 'error', text: getErrorMessage(error, '删除失败') });
      setTimeout(() => setTrashMsg(null), 3000);
    }
  };

  // 回收站 tab 激活时加载
  useEffect(() => {
    if (isOpen && activeTab === 'trash') {
      loadTrash();
    }
  }, [isOpen, activeTab, loadTrash]);

  // 提词管理 tab 激活时加载
  useEffect(() => {
    if (isOpen && activeTab === 'prompt') {
      loadPromptConfig();
    }
  }, [isOpen, activeTab, loadPromptConfig]);

  const formatDeletedTime = (iso: string | null): string => {
    if (!iso) return '-';
    const d = parseUTC(iso);
    if (!d) return '-';
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const diffDays = Math.floor(diffMs / 86400000);
    if (diffDays === 0) return '今天';
    if (diffDays === 1) return '昨天';
    if (diffDays < 7) return `${diffDays} 天前`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} 周前`;
    return fmtDateCST(iso);
  };

  // 预计算默认模型信息：系统默认模型来自 system_config 中的 model 配置
  const defaultModelLabel = useMemo(
    () => systemModelName ? `系统默认（${systemModelName}）` : '系统默认',
    [systemModelName],
  );

  if (!isOpen) return null;

  const roleLabels: Record<string, string> = {
    admin: '管理员',
    enterprise: '企业用户',
    agent: '代理用户',
    normal: '普通用户',
  };

  return (
    <>
    <div className="fixed inset-0 z-[100] flex items-start justify-center pt-[5vh]">
      {/* 遮罩层 */}
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* 弹窗 */}
      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-[1046px] mx-4 max-h-[90vh] flex flex-col">
        {/* 头部 */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="flex items-center space-x-2">
            <Cog6ToothIcon className="w-5 h-5 text-gray-500" />
            <h2 className="text-lg font-semibold text-gray-900">我的设置</h2>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Tab 导航 */}
        <div className="flex border-b border-gray-200 px-6">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.key
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* 内容区 */}
        <div className="px-6 py-5 space-y-4 overflow-y-auto flex-1">
          {/* ===== 账户信息 ===== */}
          {activeTab === 'account' && (
            <>
              {/* 用户基本信息 */}
              <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">用户名</span>
                  <span className="text-gray-900 font-medium">{user?.username}</span>
                </div>
                <div className="flex justify-between text-sm items-center">
                  <span className="text-gray-500">显示名称</span>
                  {profileEditing ? (
                    <input
                      type="text"
                      value={editDisplayName}
                      onChange={(e) => setEditDisplayName(e.target.value)}
                      maxLength={100}
                      className="w-48 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    />
                  ) : (
                    <span className="text-gray-900">{user?.display_name || '-'}</span>
                  )}
                </div>
                <div className="flex justify-between text-sm items-center">
                  <span className="text-gray-500">邮箱</span>
                  {profileEditing ? (
                    <input
                      type="email"
                      value={editEmail}
                      onChange={(e) => setEditEmail(e.target.value)}
                      maxLength={200}
                      placeholder="请输入邮箱"
                      className="w-48 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    />
                  ) : (
                    <span className="text-gray-900">{user?.email || '-'}</span>
                  )}
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">用户类型</span>
                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                    isAdmin ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'
                  }`}>
                    {roleLabels[user?.role || ''] || user?.role}
                  </span>
                </div>

                {/* 编辑/保存按钮 */}
                <div className="pt-1 border-t border-gray-200">
                  {profileEditing ? (
                    <div className="flex items-center gap-2">
                      <button
                        onClick={async () => {
                          setProfileSaving(true);
                          setProfileMsg(null);
                          const result = await updateProfile({
                            display_name: editDisplayName || undefined,
                            email: editEmail || undefined,
                          });
                          setProfileMsg({ type: result.success ? 'success' : 'error', text: result.message });
                          setProfileSaving(false);
                          if (result.success) {
                            setProfileEditing(false);
                          }
                        }}
                        disabled={profileSaving}
                        className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 transition-colors"
                      >
                        {profileSaving ? '保存中...' : '保存'}
                      </button>
                      <button
                        onClick={() => {
                          setProfileEditing(false);
                          setProfileMsg(null);
                        }}
                        className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded text-gray-600 bg-gray-200 hover:bg-gray-300 transition-colors"
                      >
                        取消
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => {
                        setEditDisplayName(user?.display_name || '');
                        setEditEmail(user?.email || '');
                        setProfileMsg(null);
                        setProfileEditing(true);
                      }}
                      className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded text-blue-600 bg-blue-50 hover:bg-blue-100 border border-blue-200 transition-colors"
                    >
                      <PencilSquareIcon className="h-3.5 w-3.5 mr-1" />
                      编辑信息
                    </button>
                  )}
                  {profileMsg && (
                    <span className={`ml-3 text-xs ${profileMsg.type === 'success' ? 'text-green-600' : 'text-red-500'}`}>
                      {profileMsg.text}
                    </span>
                  )}
                </div>
              </div>

              {/* 推荐链接 */}
              {referralCode && (
                <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg p-4 border border-purple-100">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <ShareIcon className="w-4 h-4 text-purple-500" />
                      <span className="text-sm font-medium text-purple-800">推荐有礼</span>
                      {referralReward > 0 && (
                        <span className="text-xs text-purple-600 bg-purple-100 px-2 py-0.5 rounded-full">
                          已获 {referralReward.toFixed(0)} 积分
                        </span>
                      )}
                    </div>
                    <button
                      onClick={async () => {
                        if (!showInvitedUsers) {
                          setInvitedLoading(true);
                          setShowInvitedUsers(true);
                          try {
                            const res = await authApi.getReferralStats();
                            setInvitedUsers(res.data.invited_users);
                          } catch {
                            setInvitedUsers(null);
                          } finally {
                            setInvitedLoading(false);
                          }
                        } else {
                          setShowInvitedUsers(false);
                        }
                      }}
                      className="inline-flex items-center px-2.5 py-1 text-xs font-medium rounded text-purple-700 bg-purple-100 hover:bg-purple-200 transition-colors flex-shrink-0"
                    >
                      <UserCircleIcon className="w-3.5 h-3.5 mr-1" />
                      {showInvitedUsers ? '收起' : '查看我推荐的人'}
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mb-2">
                    分享推荐链接给好友，好友注册后双方均可获得积分奖励
                  </p>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 bg-white rounded-lg border border-purple-200 px-3 py-2 text-sm text-gray-600 truncate select-all">
                      {window.location.origin}/login?ref={referralCode}
                    </div>
                    <button
                      onClick={async () => {
                        try {
                          await navigator.clipboard.writeText(
                            `${window.location.origin}/login?ref=${referralCode}`
                          );
                          setReferralCopied(true);
                          setTimeout(() => setReferralCopied(false), 2000);
                        } catch {
                          // fallback
                        }
                      }}
                      className="inline-flex items-center px-3 py-2 text-xs font-medium rounded-lg text-white bg-purple-600 hover:bg-purple-700 transition-colors flex-shrink-0"
                    >
                      {referralCopied ? (
                        <>
                          <CheckCircleIcon className="w-3.5 h-3.5 mr-1" />
                          已复制
                        </>
                      ) : (
                        <>
                          <ClipboardDocumentIcon className="w-3.5 h-3.5 mr-1" />
                          复制链接
                        </>
                      )}
                    </button>
                    <button
                      onClick={() => {
                        setShowPoster(true);
                        // 打开海报时拉取后端配置的标语，失败则回退内置默认
                        systemConfigApi.getConfig().then((res: any) => {
                          const raw = res?.data?.config?.poster_slogans;
                          if (typeof raw === 'string' && raw.trim()) {
                            try {
                              const parsed = JSON.parse(raw);
                              if (Array.isArray(parsed) && parsed.length) {
                                setPosterSlogans(
                                  parsed.map((s: any, i: number) => ({
                                    id: typeof s.label === 'string' ? s.label : `s${i}`,
                                    label: s.label ?? `标语 ${i + 1}`,
                                    text: s.text ?? '',
                                  }))
                                );
                                return;
                              }
                            } catch {
                              // 忽略，回退默认
                            }
                          }
                          setPosterSlogans(null);
                        }).catch(() => setPosterSlogans(null));
                      }}
                      className="inline-flex items-center px-3 py-2 text-xs font-medium rounded-lg text-purple-700 bg-purple-100 hover:bg-purple-200 transition-colors flex-shrink-0"
                    >
                      <PhotoIcon className="w-3.5 h-3.5 mr-1" />
                      生成海报
                    </button>
                  </div>

                  {/* 推荐用户列表 */}
                  {showInvitedUsers && (
                    <div className="mt-3 border-t border-purple-200 pt-3">
                      {invitedLoading ? (
                        <div className="text-center py-3 text-xs text-gray-400">加载中...</div>
                      ) : !invitedUsers || invitedUsers.length === 0 ? (
                        <div className="text-center py-3 text-xs text-gray-400">暂无推荐的用户</div>
                      ) : (
                        <div className="space-y-1.5 max-h-48 overflow-y-auto">
                          {invitedUsers.map((u) => (
                            <div
                              key={u.id}
                              className="flex items-center justify-between px-3 py-2 bg-white/60 rounded border border-purple-100 text-sm"
                            >
                              <div className="flex items-center gap-2 min-w-0">
                                <div className="w-7 h-7 rounded-full bg-purple-200 flex items-center justify-center flex-shrink-0">
                                  <span className="text-xs font-medium text-purple-700">
                                    {(u.display_name || u.username).charAt(0).toUpperCase()}
                                  </span>
                                </div>
                                <div className="min-w-0">
                                  <div className="text-gray-800 truncate text-xs font-medium">
                                    {u.display_name || u.username}
                                  </div>
                                  <div className="text-gray-400 text-[10px]">
                                    {u.created_at ? fmtDateCST(u.created_at) : '-'} 注册
                                  </div>
                                </div>
                              </div>
                              <span className="text-purple-600 text-xs font-medium flex-shrink-0 ml-2">
                                {u.total_points_used > 0
                                  ? `+${(u.total_points_used / 10000).toFixed(1)}万积分`
                                  : '暂无消费'}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* 统计卡片 */}
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-blue-700">{stats?.project_count ?? '-'}</div>
                  <div className="text-xs text-blue-600 mt-1">项目个数</div>
                </div>
                <div className="bg-gradient-to-br from-amber-50 to-amber-100 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-amber-700">
                    {stats?.tokens_used != null ? (stats.tokens_used / 10000).toFixed(1) + '万' : '-'}
                  </div>
                  <div className="text-xs text-amber-600 mt-1">已用积分</div>
                </div>
                <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-green-700">
                    {stats?.tokens_balance != null ? (stats.tokens_balance / 10000).toFixed(1) + '万' : '-'}
                  </div>
                  <div className="text-xs text-green-600 mt-1">剩余积分</div>
                </div>
              </div>

            </>
          )}

          {/* ===== 模型选择 ===== */}
          {activeTab === 'model' && (
            <div className="space-y-4">
              <div className="text-sm text-gray-600">
                选择您要使用的大模型，所有 AI 生成任务将使用此模型。
              </div>

              {modelMsg && (
                <div className={`px-4 py-2.5 rounded-lg text-sm ${
                  modelMsg.type === 'success'
                    ? 'bg-green-50 text-green-700 border border-green-200'
                    : 'bg-red-50 text-red-700 border border-red-200'
                }`}>
                  {modelMsg.text}
                </div>
              )}

              {modelLoading ? (
                <div className="text-center py-8 text-sm text-gray-400">加载中...</div>
              ) : (
                <div className="space-y-2">
                  {/* 系统默认选项 */}
                  <button
                    onClick={() => handleSelectModel(null)}
                    className={`w-full flex items-center justify-between px-4 py-3 border rounded-lg transition-colors ${
                      selectedModelId === null
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="text-left">
                      <div className="flex items-center gap-2">
                        <SparklesIcon className="w-4 h-4 text-gray-500" />
                        <span className="font-medium text-gray-900 text-sm">{defaultModelLabel}</span>
                        {selectedModelId === null && (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700">
                            当前
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-gray-400 mt-0.5 ml-6">使用管理员配置的默认模型</div>
                    </div>
                  </button>

                  {availableModels.map((m) => (
                    <button
                      key={m.id}
                      onClick={() => handleSelectModel(m.id)}
                      className={`w-full flex items-center justify-between px-4 py-3 border rounded-lg transition-colors ${
                        selectedModelId === m.id
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="text-left">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-gray-900 text-sm">{m.name}</span>
                          {m.is_default && (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700">
                              默认
                            </span>
                          )}
                          {selectedModelId === m.id && (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700">
                              当前
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-gray-400 mt-0.5">{m.model}</div>
                      </div>
                    </button>
                  ))}

                  {availableModels.length === 0 && (
                    <div className="text-center py-8 text-sm text-gray-400">
                      暂无可选模型，请联系管理员配置
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* ===== 修改密码 ===== */}
          {activeTab === 'password' && (
            <div className="space-y-4">
              {passwordMsg && (
                <div className={`px-4 py-2.5 rounded-lg text-sm ${
                  passwordMsg.type === 'success'
                    ? 'bg-green-50 text-green-700 border border-green-200'
                    : 'bg-red-50 text-red-700 border border-red-200'
                }`}>
                  {passwordMsg.text}
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">原密码</label>
                <div className="relative">
                  <input
                    type={showOldPassword ? 'text' : 'password'}
                    value={oldPassword}
                    onChange={(e) => setOldPassword(e.target.value)}
                    className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="请输入原密码"
                  />
                  <button
                    type="button"
                    onClick={() => setShowOldPassword(!showOldPassword)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                    title={showOldPassword ? '隐藏密码' : '显示密码'}
                  >
                    {showOldPassword ? <EyeSlashIcon className="w-4 h-4" /> : <EyeIcon className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">新密码</label>
                <div className="relative">
                  <input
                    type={showNewPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="至少6位"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                    title={showNewPassword ? '隐藏密码' : '显示密码'}
                  >
                    {showNewPassword ? <EyeSlashIcon className="w-4 h-4" /> : <EyeIcon className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">确认新密码</label>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="再次输入新密码"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                    title={showConfirmPassword ? '隐藏密码' : '显示密码'}
                  >
                    {showConfirmPassword ? <EyeSlashIcon className="w-4 h-4" /> : <EyeIcon className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <button
                onClick={handleChangePassword}
                disabled={passwordLoading}
                className="w-full inline-flex items-center justify-center px-4 py-2.5 border border-transparent text-sm font-medium rounded-lg text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {passwordLoading ? '修改中...' : '修改密码'}
              </button>
            </div>
          )}

          {/* ===== 充值 ===== */}
          {activeTab === 'recharge' && (
            <div className="space-y-4">
              {/* 支付方式选择 */}
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500">支付方式</span>
                {PAY_METHODS.map((m) => (
                  <button
                    key={m.key}
                    onClick={() => setPayMethod(m.key as 'alipay' | 'wechat')}
                    className={`px-3 py-1.5 text-sm rounded-lg border transition-colors ${
                      payMethod === m.key ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-200 text-gray-600 hover:border-gray-300'
                    }`}
                  >
                    {m.label}
                  </button>
                ))}
              </div>
              {/* 余额 + 充值套餐 — 放在同一行 */}
              <div className="flex items-stretch gap-3">
                {/* 余额显示（紧凑） */}
                <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg px-4 py-3 flex items-center gap-2 flex-shrink-0">
                  <span className="text-sm text-green-600">当前剩余</span>
                  <span className="text-xl font-bold text-green-700">
                    {stats?.tokens_balance != null ? (stats.tokens_balance / 10000).toFixed(1) : '0'}
                    <span className="text-sm font-normal ml-0.5">万积分</span>
                  </span>
                </div>

                {/* 3 个充值按钮（紧凑横排） */}
                <div className="flex gap-2 flex-1 min-w-0">
                  {plans.map((plan) => (
                    <button
                      key={plan.key}
                      onClick={() => handleRecharge(plan.key)}
                      disabled={rechargeLoading !== null}
                      className="flex-1 flex flex-col items-center justify-center px-2 py-2.5 border border-gray-200 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed min-w-0"
                    >
                      <div className="font-medium text-gray-900 text-sm">
                        {plan.name}
                      </div>
                      <div className="text-xs text-gray-400 mt-0.5">{(plan.points / 10000).toFixed(0)}万积分</div>
                      <div className="text-xs text-blue-600 font-medium mt-1">
                        {rechargeLoading === plan.key ? '处理中...' : '立即支付'}
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {rechargeMsg && (
                <div className={`px-4 py-2.5 rounded-lg text-sm ${
                  rechargeMsg.type === 'success'
                    ? 'bg-green-50 text-green-700 border border-green-200'
                    : 'bg-red-50 text-red-700 border border-red-200'
                }`}>
                  {rechargeMsg.text}
                </div>
              )}

              {/* 支付区（下单后展示：订单信息 + 打开支付页 + 取消） */}
              {payOrder && (
                <div className={`border rounded-lg p-4 space-y-3 ${payDone ? 'border-green-300 bg-green-50' : 'border-blue-200 bg-blue-50'}`}>
                  <div className="flex items-center justify-between">
                    {payDone ? (
                      <span className="text-sm font-bold text-green-700 flex items-center gap-1.5">
                        <CheckCircleIcon className="w-5 h-5" /> 已支付完成
                      </span>
                    ) : (
                      <span className="text-sm font-medium text-gray-800">等待支付</span>
                    )}
                    <span className={`text-sm font-bold ${payDone ? 'text-green-700' : 'text-blue-700'}`}>¥{payOrder.amount.toFixed(2)} / {payOrder.points.toLocaleString()} 积分</span>
                  </div>
                  {payDone ? (
                    <div className="text-sm text-green-700">积分已到账，余额已更新。</div>
                  ) : payOrder.qrcode_img ? (
                    <div className="flex flex-col items-center gap-2 py-1">
                      <img src={payOrder.qrcode_img} alt="支付二维码" className="w-44 h-44 rounded border border-gray-200 bg-white" />
                      <div className="text-xs text-gray-500">请用微信「扫一扫」完成支付，支付后自动到账</div>
                    </div>
                  ) : (
                    <div className="text-xs text-gray-500">订单号：{payOrder.order_no}（已在新窗口打开支付页，完成支付后自动到账）</div>
                  )}
                  {!payDone && (
                    <div className="flex items-center gap-2 pt-1">
                      {payOrder.pay_url && (
                        <button onClick={() => window.open(payOrder.pay_url as string, '_blank')} className="px-3 py-1.5 text-sm rounded-lg text-white bg-blue-600 hover:bg-blue-700">
                          重新打开支付页
                        </button>
                      )}
                      {payOrder.pay_page_url && (
                        <button onClick={() => window.open(`https://api.fahua.vip${payOrder.pay_page_url}`, '_blank')} className="px-3 py-1.5 text-sm rounded-lg text-gray-600 bg-white border border-gray-300 hover:bg-gray-50">
                          平台收银台
                        </button>
                      )}
                      <button onClick={handleCancelPay} className="px-3 py-1.5 text-sm rounded-lg text-gray-600 bg-white border border-gray-300 hover:bg-gray-50">
                        取消支付
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* 子Tab: 消费明细 / 项目消费 */}
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="flex items-center border-b border-gray-200 bg-gray-50">
                  <button
                    onClick={() => setRechargeSubTab('transactions')}
                    className={`px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px ${
                      rechargeSubTab === 'transactions'
                        ? 'text-blue-600 border-blue-500 bg-white'
                        : 'text-gray-500 border-transparent hover:text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    消费明细
                  </button>
                  <button
                    onClick={() => setRechargeSubTab('projectStats')}
                    className={`px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px ${
                      rechargeSubTab === 'projectStats'
                        ? 'text-blue-600 border-blue-500 bg-white'
                        : 'text-gray-500 border-transparent hover:text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    项目消费
                  </button>
                </div>

                {/* ===== 消费明细面板 ===== */}
                {rechargeSubTab === 'transactions' && (
                <div className="p-3">
                  {/* 筛选栏 */}
                  <div className="flex flex-wrap items-center gap-2 mb-3">
                    <input
                      type="date"
                      value={transStartDate}
                      onChange={(e) => setTransStartDate(e.target.value)}
                      className="px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-400"
                      placeholder="开始日期"
                    />
                    <span className="text-xs text-gray-400">至</span>
                    <input
                      type="date"
                      value={transEndDate}
                      onChange={(e) => setTransEndDate(e.target.value)}
                      className="px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-400"
                      placeholder="结束日期"
                    />
                    <select
                      value={transTypeFilter}
                      onChange={(e) => setTransTypeFilter(e.target.value)}
                      className="px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-400"
                    >
                      <option value="">全部类型</option>
                      <option value="consume">消费</option>
                      <option value="recharge">充值</option>
                      <option value="referral">推荐奖励</option>
                    </select>
                    <button
                      onClick={handleSearchTransactions}
                      className="px-3 py-1 text-xs font-medium rounded text-white bg-blue-500 hover:bg-blue-600 transition-colors"
                    >
                      查询
                    </button>
                  </div>

                  {/* 汇总信息 */}
                  <div className="flex flex-wrap gap-x-4 gap-y-1 mb-3 text-xs text-gray-500 bg-gray-50 rounded px-3 py-2">
                    <span>当前积分: <b className="text-green-600">{transBalance.toLocaleString()}</b></span>
                    <span>累计消费: <b className="text-amber-600">{transConsumeTotal.toLocaleString()}</b></span>
                    <span>累计充值: <b className="text-blue-600">{transRechargeTotal.toLocaleString()}</b></span>
                    {transReferralTotal > 0 && (
                      <span>推荐奖励: <b className="text-purple-600">{transReferralTotal.toLocaleString()}</b></span>
                    )}
                    <span>共 <b>{transTotal}</b> 条</span>
                  </div>

                  {/* 列表 */}
                  {transactionsLoading ? (
                    <div className="text-center py-8 text-sm text-gray-400">
                      <ArrowPathIcon className="h-5 w-5 mx-auto mb-2 animate-spin" />
                      加载中...
                    </div>
                  ) : transactions.length === 0 ? (
                    <div className="text-center py-8 text-sm text-gray-400">
                      <ClockIcon className="h-6 w-6 mx-auto mb-2 text-gray-300" />
                      暂无流水记录
                    </div>
                  ) : (
                    <>
                      <div className="space-y-1 max-h-80 overflow-y-auto">
                        {transactions.map((tx) => (
                          <div
                            key={tx.id}
                            className="flex items-center justify-between px-3 py-2 rounded hover:bg-gray-50 text-sm border-b border-gray-50 last:border-b-0"
                          >
                            <div className="flex-1 min-w-0 mr-3">
                              <div className="flex items-center gap-1.5">
                                <span className={`inline-block w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                                  tx.type === 'consume' ? 'bg-amber-400' :
                                  tx.type === 'recharge' ? 'bg-green-400' : 'bg-purple-400'
                                }`} />
                                <span className="text-xs text-gray-500 flex-shrink-0">
                                  {tx.created_at ? fmtDateTimeShortCST(tx.created_at) : '-'}
                                </span>
                                {tx.type === 'consume' ? (
                                  <>
                                    {tx.description ? (
                                      <span className="text-gray-800 font-medium truncate">{tx.description}</span>
                                    ) : (
                                      <span className="text-gray-700 truncate">LLM 调用消费</span>
                                    )}
                                    {tx.tokens_used > 0 && (
                                      <span className="text-xs text-gray-400 flex-shrink-0">
                                        {tx.tokens_used.toLocaleString()} tokens
                                      </span>
                                    )}
                                  </>
                                ) : (
                                  <span className="text-gray-700 truncate">
                                    {tx.description || (
                                      tx.type === 'recharge' ? '充值' : '推荐奖励'
                                    )}
                                  </span>
                                )}
                              </div>
                              {tx.type === 'consume' && (tx.project_name || tx.model_name) && (
                                <div className="mt-0.5 flex items-center gap-1 flex-wrap">
                                  {tx.project_name && (
                                    <span className="inline-flex items-center text-xs text-green-700 bg-green-50 rounded px-1.5 py-px">
                                      {tx.project_name}
                                    </span>
                                  )}
                                  {tx.model_name && (
                                    <span className="inline-flex items-center text-xs text-blue-600 bg-blue-50 rounded px-1.5 py-px">
                                      {tx.model_name}
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>
                            <span className={`text-xs font-medium flex-shrink-0 ${
                              tx.points_change > 0 ? 'text-green-600' : 'text-amber-600'
                            }`}>
                              {tx.points_change > 0 ? '+' : ''}{tx.points_change.toLocaleString()}
                            </span>
                          </div>
                        ))}
                      </div>

                      {/* 分页 */}
                      {transTotal > transPageSize && (
                        <div className="flex items-center justify-between mt-3 pt-2 border-t border-gray-100">
                          <span className="text-xs text-gray-400">
                            第 {transPage} / {Math.ceil(transTotal / transPageSize)} 页
                          </span>
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => handleTransPageChange(transPage - 1)}
                              disabled={transPage <= 1}
                              className="px-2 py-0.5 text-xs rounded border border-gray-300 disabled:opacity-30 disabled:cursor-not-allowed hover:bg-gray-100"
                            >
                              上一页
                            </button>
                            <button
                              onClick={() => handleTransPageChange(transPage + 1)}
                              disabled={transPage >= Math.ceil(transTotal / transPageSize)}
                              className="px-2 py-0.5 text-xs rounded border border-gray-300 disabled:opacity-30 disabled:cursor-not-allowed hover:bg-gray-100"
                            >
                              下一页
                            </button>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
                )}

                {/* ===== 项目消费面板 ===== */}
                {rechargeSubTab === 'projectStats' && (
                <div className="p-3">
                  {/* 筛选栏 */}
                  <div className="flex flex-wrap items-center gap-2 mb-3">
                    <input
                      type="date"
                      value={projStatsStartDate}
                      onChange={(e) => setProjStatsStartDate(e.target.value)}
                      className="px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-400"
                      placeholder="开始日期"
                    />
                    <span className="text-xs text-gray-400">至</span>
                    <input
                      type="date"
                      value={projStatsEndDate}
                      onChange={(e) => setProjStatsEndDate(e.target.value)}
                      className="px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-400"
                      placeholder="结束日期"
                    />
                    <button
                      onClick={handleSearchProjectStats}
                      className="px-3 py-1 text-xs font-medium rounded text-white bg-blue-500 hover:bg-blue-600 transition-colors"
                    >
                      查询
                    </button>
                  </div>

                  {/* 汇总 */}
                  <div className="flex flex-wrap gap-x-4 gap-y-1 mb-3 text-xs text-gray-500 bg-gray-50 rounded px-3 py-2">
                    <span>总消费积分: <b className="text-amber-600">{projectStatsTotalPoints.toLocaleString()}</b></span>
                    <span>总 Tokens: <b className="text-blue-600">{projectStatsTotalTokens.toLocaleString()}</b></span>
                    <span>共 <b>{projectStats.length}</b> 个项目</span>
                  </div>

                  {/* 项目列表 */}
                  {projectStatsLoading ? (
                    <div className="text-center py-8 text-sm text-gray-400">
                      <ArrowPathIcon className="h-5 w-5 mx-auto mb-2 animate-spin" />
                      加载中...
                    </div>
                  ) : projectStats.length === 0 ? (
                    <div className="text-center py-8 text-sm text-gray-400">
                      <FolderOpenIcon className="h-6 w-6 mx-auto mb-2 text-gray-300" />
                      暂无项目消费记录
                    </div>
                  ) : (
                    <div className="space-y-1 max-h-80 overflow-y-auto">
                      {projectStats.map((proj, idx) => {
                        const pct = projectStatsTotalPoints > 0
                          ? ((proj.points_used / projectStatsTotalPoints) * 100).toFixed(1)
                          : '0';
                        const isTool = proj.project_id === null;
                        return (
                          <div
                            key={isTool ? `tool-${idx}` : `proj-${proj.project_id}`}
                            className="flex items-center gap-3 px-3 py-2.5 rounded hover:bg-gray-50 text-sm border-b border-gray-50 last:border-b-0"
                          >
                            <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                              isTool ? 'bg-purple-100' : 'bg-blue-100'
                            }`}>
                              {isTool ? (
                                <WrenchScrewdriverIcon className="w-4 h-4 text-purple-500" />
                              ) : (
                                <span className="text-xs font-bold text-blue-600">{idx + 1}</span>
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="text-gray-800 font-medium truncate">{proj.project_name}</div>
                              <div className="flex items-center gap-3 mt-0.5">
                                <span className="text-xs text-gray-400">
                                  {proj.tokens_used.toLocaleString()} tokens
                                </span>
                                <span className="text-xs text-gray-400">
                                  {proj.consume_count} 次消费
                                </span>
                                {/* 占比进度条 */}
                                <div className="flex items-center gap-1 flex-1 min-w-0">
                                  <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden max-w-[80px]">
                                    <div
                                      className="h-full bg-amber-400 rounded-full transition-all"
                                      style={{ width: `${pct}%` }}
                                    />
                                  </div>
                                  <span className="text-xs text-gray-400 flex-shrink-0">{pct}%</span>
                                </div>
                              </div>
                            </div>
                            <span className="text-xs font-semibold text-amber-600 flex-shrink-0">
                              {proj.points_used.toLocaleString()} 积分
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
                )}
              </div>
            </div>
          )}

          {/* ===== 知识库 ===== */}
          {activeTab === 'knowledge' && (
            <div className="space-y-4">
              <div className="text-sm text-gray-600">
                管理您的知识库，上传 PDF、Word、Markdown、TXT 文件，生成标书时将引用知识库中的资料。
              </div>

              {kbMsg && (
                <div className={`px-4 py-2.5 rounded-lg text-sm ${
                  kbMsg.type === 'success'
                    ? 'bg-green-50 text-green-700 border border-green-200'
                    : 'bg-red-50 text-red-700 border border-red-200'
                }`}>
                  {kbMsg.text}
                </div>
              )}

              <div className="flex gap-4 min-h-0 flex-1">
                {/* 左侧：知识库列表 */}
                <div className="w-[240px] flex-shrink-0 flex flex-col">
                  {/* 来源筛选 */}
                  <div className="flex space-x-1 mb-2">
                    <button
                      onClick={() => { setKbSource('owned'); setSelectedKbId(null); }}
                      className={`flex-1 px-2 py-1 text-xs font-medium rounded transition-colors ${
                        kbSource === 'owned'
                          ? 'text-blue-600 bg-blue-50'
                          : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      我的
                    </button>
                    <button
                      onClick={() => { setKbSource('shared'); setSelectedKbId(null); }}
                      className={`flex-1 px-2 py-1 text-xs font-medium rounded transition-colors ${
                        kbSource === 'shared'
                          ? 'text-blue-600 bg-blue-50'
                          : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      共享
                    </button>
                  </div>
                  {/* 新建知识库按钮 / 表单（仅"我的"tab可用） */}
                  {kbSource === 'owned' && !readOnly && !showKbCreate ? (
                    <button
                      onClick={() => {
                        setShowKbCreate(true);
                        setKbEditingId(null);
                        setKbName('');
                        setKbDesc('');
                      }}
                      className="w-full inline-flex items-center justify-center px-3 py-2 border-2 border-dashed border-gray-300 rounded-lg text-xs font-medium text-gray-600 hover:border-blue-400 hover:text-blue-600 hover:bg-blue-50 transition-colors mb-2"
                    >
                      <PlusIcon className="w-3.5 h-3.5 mr-1" />
                      新建知识库
                    </button>
                  ) : kbSource === 'owned' && !readOnly && (
                    <div className="border border-blue-300 bg-blue-50/50 rounded-lg p-3 space-y-2 mb-2">
                      <div className="text-xs font-medium text-blue-700">
                        {kbEditingId ? '编辑知识库' : '新建知识库'}
                      </div>
                      <input
                        type="text"
                        value={kbName}
                        onChange={(e) => setKbName(e.target.value)}
                        placeholder="知识库名称"
                        className="w-full px-2.5 py-1.5 border border-gray-300 rounded text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        onKeyDown={(e) => e.key === 'Enter' && handleKbCreate()}
                        autoFocus
                      />
                      <textarea
                        value={kbDesc}
                        onChange={(e) => setKbDesc(e.target.value)}
                        rows={2}
                        placeholder="知识库描述（可选）"
                        className="w-full px-2.5 py-1.5 border border-gray-300 rounded text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                      />
                      <div className="flex space-x-1.5">
                        <button
                          onClick={handleKbCreate}
                          disabled={kbSaving || !kbName.trim()}
                          className="flex-1 inline-flex justify-center items-center px-2.5 py-1.5 text-xs font-medium rounded text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 transition-colors"
                        >
                          {kbSaving ? '保存中...' : (kbEditingId ? '保存修改' : '创建')}
                        </button>
                        <button
                          onClick={() => {
                            setShowKbCreate(false);
                            setKbEditingId(null);
                            setKbName('');
                            setKbDesc('');
                          }}
                          className="px-3 py-1.5 text-xs font-medium rounded text-gray-600 bg-white border border-gray-300 hover:bg-gray-50 transition-colors"
                        >
                          取消
                        </button>
                      </div>
                    </div>
                  )}

                  {/* 知识库列表 */}
                  {kbLoading ? (
                    <div className="text-center py-8 text-sm text-gray-400">加载中...</div>
                  ) : knowledgeBases.length === 0 ? (
                    <div className="flex-1 flex flex-col items-center justify-center text-center py-8">
                      <BookOpenIcon className="h-10 w-10 text-gray-300 mb-2" />
                      <p className="text-xs text-gray-400">暂无知识库</p>
                    </div>
                  ) : (
                    <div className="space-y-1.5 overflow-y-auto flex-1">
                      {knowledgeBases.map((kb) => {
                        const isSelected = selectedKbId === kb.id;
                        return (
                          <div
                            key={kb.id}
                            onClick={() => handleSelectKb(kb.id)}
                            className={`p-2.5 rounded-lg border cursor-pointer transition-colors ${
                              isSelected
                                ? 'border-blue-400 bg-blue-50'
                                : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                            }`}
                          >
                            <div className="flex items-center gap-1.5">
                              <BookOpenIcon className={`w-3.5 h-3.5 flex-shrink-0 ${isSelected ? 'text-blue-500' : 'text-gray-400'}`} />
                              <span className="text-xs font-medium text-gray-900 truncate">{kb.name}</span>
                            </div>
                            <div className="flex items-center gap-2 mt-1">
                              <span className="text-[10px] text-gray-400">{kb.file_count} 个文件</span>
                              {isAdmin && kb.owner_name && (
                                <span className="text-[10px] text-gray-400 flex items-center gap-0.5">
                                  <UserCircleIcon className="w-3 h-3" />
                                  {kb.owner_name}
                                </span>
                              )}
                              {kb.share_permission && (
                                <span className={`text-[10px] font-medium ${
                                  kb.share_permission === 'edit' ? 'text-orange-500' : 'text-blue-500'
                                }`}>
                                  {kb.share_permission === 'edit' ? '可编辑' : '只读'}
                                </span>
                              )}
                              <div className="flex items-center gap-0.5 ml-auto">
                                {kb.user_id === user?.id && !readOnly && (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setShareTarget({ type: 'knowledge_base', id: kb.id, name: kb.name, isOwner: true });
                                    }}
                                    className="p-0.5 rounded text-gray-300 hover:text-purple-500 hover:bg-purple-50 transition-colors"
                                    title="分享"
                                  >
                                    <ShareIcon className="w-3 h-3" />
                                  </button>
                                )}
                                {kb.user_id === user?.id && !readOnly && (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setTransferUsername('');
                                      setTransferCancelShares(false);
                                      setTransferCheckResult(null);
                                      setTransferTarget({ type: 'knowledge_base', id: kb.id, name: kb.name });
                                    }}
                                    className="p-0.5 rounded text-gray-300 hover:text-orange-500 hover:bg-orange-50 transition-colors"
                                    title="转移所有权"
                                  >
                                    <ArrowPathIcon className="w-3 h-3" />
                                  </button>
                                )}
                                {!readOnly && !(kbSource === 'shared' && kb.share_permission === 'read') && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setShowKbCreate(true);
                                    setKbEditingId(kb.id);
                                    setKbName(kb.name);
                                    setKbDesc(kb.description || '');
                                  }}
                                  className="p-0.5 rounded text-gray-300 hover:text-blue-500 hover:bg-blue-50 transition-colors"
                                  title="编辑"
                                >
                                  <PencilSquareIcon className="w-3 h-3" />
                                </button>
                                )}
                                {!readOnly && !(kbSource === 'shared' && kb.share_permission === 'read') && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleKbDelete(kb);
                                  }}
                                  className="p-0.5 rounded text-gray-300 hover:text-red-500 hover:bg-red-50 transition-colors"
                                  title="删除"
                                >
                                  <TrashIcon className="w-3 h-3" />
                                </button>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* 右侧：文件管理 */}
                <div className="flex-1 border-l border-gray-200 pl-4 overflow-y-auto min-h-0">
                  {selectedKbId ? (
                    (() => {
                      const selectedKb = knowledgeBases.find(k => k.id === selectedKbId);
                      if (!selectedKb) return null;
                      const isUploading = uploadingKbId === selectedKbId;
                      const selectedKbIsReadonly = readOnly || (kbSource === 'shared' && selectedKb.share_permission === 'read');
                      return (
                        <div className="space-y-3">
                          {/* 知识库头部 */}
                          <div className="flex items-center justify-between">
                            <div>
                              <h3 className="text-sm font-medium text-gray-900">{selectedKb.name}</h3>
                              {selectedKb.description && (
                                <p className="text-xs text-gray-400 mt-0.5">{selectedKb.description}</p>
                              )}
                            </div>
                            {/* 上传文件 */}
                            {!selectedKbIsReadonly && (
                            <label
                              className={`inline-flex items-center px-3 py-1.5 border-2 border-dashed rounded-lg cursor-pointer transition-colors text-xs ${
                                isUploading
                                  ? 'border-gray-300 bg-gray-50 text-gray-400'
                                  : 'border-blue-300 text-blue-600 hover:border-blue-400 hover:bg-blue-50'
                              }`}
                            >
                              <input
                                type="file"
                                className="hidden"
                                accept=".pdf,.docx,.doc,.md,.txt,.json,.jpg,.jpeg,.png,.gif,.webp,.bmp"
                                multiple
                                disabled={isUploading}
                                onChange={(e) => handleKbFileUpload(selectedKbId, e)}
                              />
                              <PlusIcon className="w-3.5 h-3.5 mr-1" />
                              {isUploading ? '上传中...' : '上传文件'}
                            </label>
                            )}
                          </div>

                          {/* 文件列表 */}
                          {kbFilesLoading ? (
                            <div className="text-center py-8 text-xs text-gray-400">加载中...</div>
                          ) : kbFiles.length === 0 ? (
                            <div className="flex flex-col items-center justify-center py-12 text-gray-400">
                              <DocumentTextIcon className="h-10 w-10 mb-2 opacity-30" />
                              <p className="text-xs">暂无文件</p>
                              <p className="text-[10px] mt-1">点击右上角"上传文件"添加</p>
                            </div>
                          ) : (
                            <div className="space-y-1.5">
                              {kbFiles.map((f) => (
                                <div key={f.id} className="bg-white rounded-lg border border-gray-100">
                                  <div className="flex items-center justify-between px-3 py-2">
                                    <div className="flex items-center space-x-2 min-w-0">
                                      {f.file_type.match(/^(jpg|jpeg|png|gif|webp|bmp)$/i) ? (
                                        <PhotoIcon className="w-4 h-4 text-purple-400 flex-shrink-0" />
                                      ) : (
                                        <DocumentTextIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />
                                      )}
                                      <div className="min-w-0">
                                        <span className="text-xs text-gray-700 truncate block">{f.original_name}</span>
                                        <span className="text-xs text-gray-400">
                                          {f.file_type.toUpperCase()} · {formatFileSize(f.file_size)}
                                          {f.image_urls && f.image_urls.length > 0 && (
                                            <span className="ml-1.5 text-purple-500">
                                              · {f.image_urls.length} 张图片
                                            </span>
                                          )}
                                        </span>
                                      </div>
                                    </div>
                                    <div className="flex items-center space-x-0.5 flex-shrink-0">
                                      {f.image_urls && f.image_urls.length > 0 && (
                                        <button
                                          onClick={() => setExpandedImageFileId(
                                            expandedImageFileId === f.id ? null : f.id
                                          )}
                                          className="p-1 rounded text-gray-400 hover:text-purple-500 hover:bg-purple-50 transition-colors"
                                          title="查看图片"
                                        >
                                          <PhotoIcon className="w-3.5 h-3.5" />
                                        </button>
                                      )}
                                      <button
                                        onClick={() => handleViewChunks(selectedKbId, f.id)}
                                        className="p-1 rounded text-gray-400 hover:text-indigo-500 hover:bg-indigo-50 transition-colors"
                                        title="查看拆分"
                                      >
                                        <ListBulletIcon className="w-3.5 h-3.5" />
                                      </button>
                                      {!selectedKbIsReadonly && (
                                      <button
                                        onClick={() => handleEditFile(selectedKbId, f)}
                                        className="p-1 rounded text-gray-400 hover:text-green-500 hover:bg-green-50 transition-colors"
                                        title="编辑内容"
                                      >
                                        <PencilSquareIcon className="w-3.5 h-3.5" />
                                      </button>
                                      )}
                                      {!selectedKbIsReadonly && (
                                      <button
                                        onClick={() => handleKbFileDelete(selectedKbId, f.id, f.original_name)}
                                        className="p-1 rounded text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                                        title="删除文件"
                                      >
                                        <TrashIcon className="w-3 h-3" />
                                      </button>
                                      )}
                                    </div>
                                  </div>

                                  {/* 图片缩略图预览 */}
                                  {expandedImageFileId === f.id && f.image_urls && f.image_urls.length > 0 && (
                                    <div className="border-t border-gray-100 px-3 py-2">
                                      <div className="flex flex-wrap gap-2">
                                        {f.image_urls.map((img, idx) => (
                                          <div
                                            key={idx}
                                            className="relative group cursor-pointer border border-gray-200 rounded-md overflow-hidden hover:border-purple-400 transition-colors"
                                            onClick={() => setImagePreviewUrl(resolveImageUrl(img.url))}
                                          >
                                            <img
                                              src={resolveImageUrl(img.url)}
                                              alt={`${f.original_name} - 图片 ${idx + 1}`}
                                              className="w-16 h-16 object-cover"
                                              loading="lazy"
                                            />
                                            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-end p-0.5">
                                              {img.page && (
                                                <span className="text-[9px] text-white bg-black/50 rounded px-1 py-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                                                  p{img.page}
                                                </span>
                                              )}
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                      <p className="text-[10px] text-gray-400 mt-1">
                                        点击缩略图放大查看
                                      </p>
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}

                          {/* 分块查看 */}
                          {chunksLoading && (
                            <div className="text-center py-6 text-sm text-gray-400">加载分块数据...</div>
                          )}
                          {chunksData && !chunksLoading && (
                            <div className="border border-indigo-200 bg-indigo-50/30 rounded-lg p-4 space-y-3">
                              <div className="flex items-center justify-between">
                                <div>
                                  <h4 className="text-sm font-medium text-indigo-700">文件分块 - {chunksData.file_name}</h4>
                                  <p className="text-xs text-gray-500 mt-0.5">
                                    共 {chunksData.total_chunks} 个分块 · {chunksData.total_chars} 字符
                                  </p>
                                </div>
                                <button
                                  onClick={() => { setChunksData(null); setExpandedChunkIndex(null); }}
                                  className="p-1 rounded text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                                >
                                  <XMarkIcon className="w-4 h-4" />
                                </button>
                              </div>
                              {chunksData.total_chunks === 0 ? (
                                <div className="text-center py-4 text-xs text-gray-400">
                                  该文件暂无文本内容可供拆分
                                </div>
                              ) : (
                                <div className="space-y-1.5 max-h-80 overflow-y-auto pr-1">
                                  {chunksData.chunks.map((chunk) => {
                                    const isExpanded = expandedChunkIndex === chunk.index;
                                    return (
                                      <div
                                        key={chunk.index}
                                        className="border border-gray-200 bg-white rounded-md overflow-hidden"
                                      >
                                        <div
                                          className="flex items-center justify-between px-3 py-2 cursor-pointer hover:bg-gray-50 transition-colors"
                                          onClick={() => setExpandedChunkIndex(isExpanded ? null : chunk.index)}
                                        >
                                          <div className="flex items-center space-x-2 min-w-0">
                                            <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-indigo-100 text-indigo-700 text-xs font-medium flex-shrink-0">
                                              {chunk.index + 1}
                                            </span>
                                            <span className="text-xs text-gray-600 truncate">{chunk.preview || '(无内容)'}</span>
                                          </div>
                                          <div className="flex items-center space-x-2 flex-shrink-0 ml-2">
                                            <span className="text-xs text-gray-400">{chunk.char_count} 字</span>
                                            <svg
                                              className={`w-3.5 h-3.5 text-gray-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                                              fill="none" viewBox="0 0 24 24" stroke="currentColor"
                                            >
                                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                            </svg>
                                          </div>
                                        </div>
                                        {isExpanded && (
                                          <div className="border-t border-gray-100 px-3 py-2 bg-gray-50">
                                            <pre className="text-xs text-gray-700 whitespace-pre-wrap font-sans leading-relaxed">
                                              {chunk.content}
                                            </pre>
                                          </div>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })()
                  ) : (
                    <div className="flex-1 flex flex-col items-center justify-center h-full text-center py-12">
                      <BookOpenIcon className="h-12 w-12 text-gray-200 mb-3" />
                      <p className="text-sm text-gray-400">请从左侧选择知识库进行管理</p>
                      <p className="text-xs text-gray-300 mt-1">选择知识库后可查看和管理其中的文件</p>
                    </div>
                  )}
                </div>
              </div>

            {/* 图片放大预览弹窗 */}
            {imagePreviewUrl && (
              <div
                className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 cursor-pointer"
                onClick={() => setImagePreviewUrl(null)}
              >
                <div className="relative max-w-[90vw] max-h-[90vh]">
                  <button
                    onClick={(e) => { e.stopPropagation(); setImagePreviewUrl(null); }}
                    className="absolute -top-8 right-0 text-white hover:text-gray-300 transition-colors"
                  >
                    <XMarkIcon className="w-6 h-6" />
                  </button>
                  <img
                    src={imagePreviewUrl}
                    alt="图片预览"
                    className="max-w-[90vw] max-h-[85vh] object-contain rounded-lg shadow-2xl"
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>
              </div>
            )}

            {/* 文件内容全屏编辑 */}
            <ExtractedTextEditor
              open={!!editingFile}
              loading={editLoading}
              title={editingFile?.original_name || ''}
              value={editContent}
              onChange={setEditContent}
              imageGallery={allKbImages}
              onImageUpload={handleKbImageUpload}
              resolveUrl={resolveImageUrl}
              onSave={handleSaveEdit}
              onClose={() => { setEditingFile(null); setEditingKbId(null); setEditContent(''); setAllKbImages([]); }}
            />
          </div>
        )}

          {/* ===== 任务列表 ===== */}
          {activeTab === 'tasks' && (
            <div className="space-y-3">
              {taskMsg && (
                <div className={`px-4 py-2.5 rounded-lg text-sm ${
                  taskMsg.type === 'success'
                    ? 'bg-green-50 text-green-700 border border-green-200'
                    : 'bg-red-50 text-red-700 border border-red-200'
                }`}>
                  {taskMsg.text}
                </div>
              )}

              {taskList.length === 0 ? (
                <div className="text-center py-12">
                  <DocumentArrowDownIcon className="mx-auto h-12 w-12 text-gray-300" />
                  <p className="mt-3 text-sm text-gray-500">暂无导出任务</p>
                  <p className="mt-1 text-xs text-gray-400">在标书内容页面点击"导出Word"创建新任务</p>
                </div>
              ) : (
                <>
                  {taskList.map(task => (
                    <div
                      key={task.task_id}
                      className={`border rounded-lg p-4 transition-colors ${
                        task.status === 'completed'
                          ? 'border-green-200 bg-green-50/30'
                          : task.status === 'failed'
                          ? 'border-red-200 bg-red-50/30'
                          : 'border-blue-200 bg-blue-50/30'
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          {/* 任务标题 */}
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-sm font-medium text-gray-900 truncate">
                              #{task.task_id} {task.project_name}
                            </span>
                            {/* 状态标签 */}
                            <span className={`flex-shrink-0 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                              task.status === 'completed'
                                ? 'bg-green-100 text-green-700'
                                : task.status === 'failed'
                                ? 'bg-red-100 text-red-700'
                                : 'bg-blue-100 text-blue-700'
                            }`}>
                              {task.status === 'completed' && <CheckCircleIcon className="w-3 h-3 mr-1" />}
                              {task.status === 'failed' && <ExclamationCircleIcon className="w-3 h-3 mr-1" />}
                              {task.status === 'running' && (
                                <svg className="animate-spin w-3 h-3 mr-1" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                                </svg>
                              )}
                              {task.status === 'running' ? '生成中' : task.status === 'completed' ? '已完成' : '失败'}
                            </span>
                            {task.downloaded && task.status === 'completed' && (
                              <span className="flex-shrink-0 text-xs text-gray-400">已下载</span>
                            )}
                          </div>

                          {/* 进度信息 */}
                          {task.status === 'running' && (
                            <div className="mt-2">
                              <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
                                <span>{task.current}</span>
                                <span>{task.completed}/{task.total}</span>
                              </div>
                              <div className="w-full bg-gray-200 rounded-full h-1.5">
                                <div
                                  className="bg-blue-600 h-1.5 rounded-full transition-all duration-500"
                                  style={{ width: `${task.total > 0 ? (task.completed / task.total) * 100 : 0}%` }}
                                />
                              </div>
                            </div>
                          )}

                          {/* 错误信息 */}
                          {task.status === 'failed' && task.error_message && (
                            <details className="mt-1">
                              <summary className="text-xs text-red-600 cursor-pointer hover:text-red-500">
                                错误详情（点击展开）
                              </summary>
                              <pre className="mt-1 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-700 whitespace-pre-wrap max-h-40 overflow-y-auto">
                                {task.error_message}
                              </pre>
                            </details>
                          )}

                          {/* 时间信息 */}
                          <div className="mt-2 flex items-center gap-3 text-xs text-gray-400">
                            {isAdmin && task.user_id != null && task.user_id !== user?.id && (
                              <span className="text-purple-500 bg-purple-50 px-1.5 py-0.5 rounded" title="其他用户创建">
                                用户 #{task.user_id}
                              </span>
                            )}
                            <span>创建: {formatTaskTime(task.created_at)}</span>
                            {task.completed_at && (
                              <span className="text-green-500">完成: {formatTaskTime(task.completed_at)}</span>
                            )}
                            {task.failed_at && (
                              <span className="text-red-500">失败: {formatTaskTime(task.failed_at)}</span>
                            )}
                            {task.status === 'completed' && task.file_size > 0 && (
                              <span>文件大小: {formatFileSize(task.file_size)}</span>
                            )}
                          </div>
                        </div>

                        {/* 操作按钮 */}
                        <div className="flex items-center gap-1.5 ml-3 flex-shrink-0">
                          {task.status === 'completed' && !task.downloaded && (
                            <button
                              onClick={() => handleTaskDownload(task)}
                              className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md text-white bg-red-500 hover:bg-red-600 transition-colors animate-pulse"
                            >
                              <DocumentArrowDownIcon className="w-3.5 h-3.5 mr-1" />
                              下载
                            </button>
                          )}
                          {task.status === 'completed' && task.downloaded && (
                            <button
                              onClick={() => handleTaskDownload(task)}
                              className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md text-gray-600 bg-gray-100 hover:bg-gray-200 transition-colors"
                            >
                              <DocumentArrowDownIcon className="w-3.5 h-3.5 mr-1" />
                              下载
                            </button>
                          )}
                          {/* 删除按钮：仅管理员或任务创建者可见 */}
                          {(isAdmin || task.user_id === user?.id) && (
                            <button
                              onClick={() => handleTaskDelete(task.task_id)}
                              className="p-1.5 rounded text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                              title="删除任务"
                            >
                              <TrashIcon className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </>
              )}

              {/* 底部统计 */}
              {taskList.length > 0 && (
                <div className="pt-2 flex items-center justify-between text-xs text-gray-400">
                  <span>共 {taskList.length} 个任务</span>
                  <button
                    onClick={() => {
                      const toDelete = taskList.filter(t =>
                        t.status !== 'running' &&
                        (isAdmin || t.user_id === user?.id)
                      );
                      Promise.all(toDelete.map(t => handleTaskDelete(t.task_id)));
                    }}
                    className="text-gray-500 hover:text-red-600 transition-colors"
                  >
                    清空已完成/失败任务
                  </button>
                </div>
              )}
            </div>
          )}

          {/* ===== 企业管理 ===== */}
          {activeTab === 'company' && (
            <div className="flex gap-4 min-h-0 flex-1">
              {/* 左侧：企业列表 */}
              <div className="w-[240px] flex-shrink-0 flex flex-col">
                {/* 来源筛选 */}
                <div className="flex space-x-1 mb-2">
                  <button
                    onClick={() => { setCompanySource('owned'); setSelectedCompanyId(null); }}
                    className={`flex-1 px-2 py-1 text-xs font-medium rounded transition-colors ${
                      companySource === 'owned'
                        ? 'text-blue-600 bg-blue-50'
                        : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    我的
                  </button>
                  <button
                    onClick={() => { setCompanySource('shared'); setSelectedCompanyId(null); }}
                    className={`flex-1 px-2 py-1 text-xs font-medium rounded transition-colors ${
                      companySource === 'shared'
                        ? 'text-blue-600 bg-blue-50'
                        : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    共享
                  </button>
                </div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700">企业列表</span>
                  {companySource === 'owned' && !readOnly && (
                  <button
                    onClick={() => {
                      setSelectedCompanyId('new');
                      setCompanySubTab('basic');
                      resetCompanyForm();
                    }}
                    className="inline-flex items-center px-2 py-1 text-xs font-medium rounded text-white bg-blue-600 hover:bg-blue-700 transition-colors"
                  >
                    <PlusIcon className="w-3 h-3 mr-0.5" />
                    新增
                  </button>
                  )}
                </div>

                {companyLoading ? (
                  <div className="text-center py-8 text-sm text-gray-400">加载中...</div>
                ) : companyProfiles.length === 0 ? (
                  <div className="flex-1 flex flex-col items-center justify-center text-center py-8">
                    <BuildingOffice2Icon className="h-10 w-10 text-gray-300 mb-2" />
                    <p className="text-xs text-gray-400">暂无企业信息</p>
                  </div>
                ) : (
                  <div className="space-y-1.5 overflow-y-auto flex-1">
                    {companyProfiles.map((cp) => (
                      <div
                        key={cp.id}
                        onClick={() => {
                          setSelectedCompanyId(cp.id);
                          setCompanySubTab('basic');
                          setCompanyForm({
                            name: cp.name,
                            address: cp.address || '',
                            legal_representative: cp.legal_representative || '',
                            legal_representative_id_number: cp.legal_representative_id_number || '',
                            unified_social_credit_code: cp.unified_social_credit_code || '',
                            bank_name: cp.bank_name || '',
                            bank_account: cp.bank_account || '',
                            company_phone: cp.company_phone || '',
                            fax: cp.fax || '',
                            postal_code: cp.postal_code || '',
                            notes: cp.notes || '',
                          });
                        }}
                        className={`p-2.5 rounded-lg border cursor-pointer transition-colors ${
                          selectedCompanyId === cp.id
                            ? 'border-blue-400 bg-blue-50'
                            : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        <div className="flex items-center gap-1.5">
                          <BuildingOffice2Icon className={`w-3.5 h-3.5 flex-shrink-0 ${selectedCompanyId === cp.id ? 'text-blue-500' : 'text-gray-400'}`} />
                          <span className="text-sm font-medium text-gray-900 truncate">{cp.name}</span>
                        </div>
                        <div className="flex items-center gap-2 mt-1">
                          {cp.is_default && (
                            <span className="inline-flex items-center px-1 py-0.5 rounded text-[10px] font-medium bg-amber-100 text-amber-700">
                              <StarSolidIcon className="w-2.5 h-2.5 mr-0.5" />
                              默认
                            </span>
                          )}
                          {cp.share_permission && (
                            <span className={`text-[10px] font-medium ${
                              cp.share_permission === 'edit' ? 'text-orange-500' : 'text-blue-500'
                            }`}>
                              {cp.share_permission === 'edit' ? '可编辑' : '只读'}
                            </span>
                          )}
                          <span className="text-[10px] text-gray-400">{cp.project_count} 个项目</span>
                          {isAdmin && cp.owner_name && (
                            <span className="text-[10px] text-gray-400 flex items-center gap-0.5">
                              <UserCircleIcon className="w-3 h-3" />
                              {cp.owner_name}
                            </span>
                          )}
                          {cp.user_id === user?.id && !readOnly && (
                            <>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setTransferUsername('');
                                  setTransferCancelShares(false);
                                  setTransferCheckResult(null);
                                  setTransferTarget({ type: 'company_profile', id: cp.id, name: cp.name });
                                }}
                                className="ml-auto p-0.5 rounded text-gray-300 hover:text-orange-500 hover:bg-orange-50 transition-colors"
                                title="转移所有权"
                              >
                                <ArrowPathIcon className="w-3 h-3" />
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setShareTarget({ type: 'company_profile', id: cp.id, name: cp.name, isOwner: true });
                                }}
                                className="p-0.5 rounded text-gray-300 hover:text-purple-500 hover:bg-purple-50 transition-colors"
                                title="分享"
                              >
                                <ShareIcon className="w-3 h-3" />
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* 右侧：详情区域 */}
              <div className="flex-1 border-l border-gray-200 pl-4 overflow-y-auto min-h-0">
                {selectedCompanyId ? (
                  (() => {
                    const selectedCompany = typeof selectedCompanyId === 'number' ? companyProfiles.find(c => c.id === selectedCompanyId) : null;
                    const selectedCompanyIsReadonly = readOnly || (companySource === 'shared' && selectedCompany?.share_permission === 'read');
                    return (
                  <div className="space-y-3">
                    {/* 头部操作栏 */}
                    <div className="flex items-center justify-between">
                      <h3 className="text-sm font-medium text-gray-900">
                        {selectedCompanyId === 'new' ? '新增企业' : '企业管理'}
                      </h3>
                      {selectedCompanyId !== 'new' && !selectedCompanyIsReadonly && (
                        <div className="flex items-center space-x-1">
                          {!companyProfiles.find(c => c.id === selectedCompanyId)?.is_default && (
                            <button
                              onClick={() => handleCompanySetDefault(selectedCompanyId as number)}
                              className="inline-flex items-center px-2 py-1 text-xs font-medium rounded text-amber-700 bg-amber-50 hover:bg-amber-100 transition-colors"
                            >
                              <StarOutlineIcon className="w-3 h-3 mr-0.5" />
                              设为默认
                            </button>
                          )}
                          <button
                            onClick={() => {
                              const cp = companyProfiles.find(c => c.id === selectedCompanyId);
                              if (cp) handleCompanyDelete(cp);
                            }}
                            className="inline-flex items-center px-2 py-1 text-xs font-medium rounded text-red-600 bg-red-50 hover:bg-red-100 transition-colors"
                          >
                            <TrashIcon className="w-3 h-3 mr-0.5" />
                            删除
                          </button>
                        </div>
                      )}
                    </div>

                    {companyMsg && (
                      <div className={`px-3 py-2 rounded-lg text-xs ${
                        companyMsg.type === 'success'
                          ? 'bg-green-50 text-green-700 border border-green-200'
                          : 'bg-red-50 text-red-700 border border-red-200'
                      }`}>
                        {companyMsg.text}
                      </div>
                    )}

                    {/* 子Tab导航（仅编辑已有企业时显示） */}
                    {selectedCompanyId !== 'new' && (
                      <div className="flex border-b border-gray-200 space-x-1">
                        {[
                          { key: 'basic', label: '企业基本信息', icon: BuildingOffice2Icon },
                          { key: 'financial', label: '企业财务信息', icon: BanknotesIcon },
                          { key: 'qualifications', label: '企业资质', icon: ShieldCheckIcon },
                          { key: 'certificates', label: '企业相关证明', icon: ClipboardDocumentIcon },
                          { key: 'other', label: '其他材料', icon: FolderOpenIcon },
                        ].map(tab => (
                          <button
                            key={tab.key}
                            onClick={() => setCompanySubTab(tab.key)}
                            className={`inline-flex items-center px-3 py-2 text-xs font-medium border-b-2 transition-colors ${
                              companySubTab === tab.key
                                ? 'border-blue-600 text-blue-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700'
                            }`}
                          >
                            <tab.icon className="w-3.5 h-3.5 mr-1" />
                            {tab.label}
                          </button>
                        ))}
                      </div>
                    )}

                    {/* === 企业基本信息 === */}
                    {(selectedCompanyId === 'new' || companySubTab === 'basic') && (
                      <div className="space-y-3">
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="block text-xs text-gray-500 mb-0.5">企业名称 *</label>
                            <input
                              type="text" value={companyForm.name}
                              onChange={(e) => setCompanyForm(f => ({ ...f, name: e.target.value }))}
                              placeholder="如：北京XX科技有限公司"
                              className="w-full px-3 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                              disabled={selectedCompanyIsReadonly}
                              autoFocus
                            />
                          </div>
                          <div>
                            <label className="block text-xs text-gray-500 mb-0.5">法定代表人</label>
                            <input
                              type="text" value={companyForm.legal_representative}
                              onChange={(e) => setCompanyForm(f => ({ ...f, legal_representative: e.target.value }))}
                              placeholder="姓名"
                              className="w-full px-3 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                              disabled={selectedCompanyIsReadonly}
                            />
                          </div>
                          <div>
                            <label className="block text-xs text-gray-500 mb-0.5">法人身份证号</label>
                            <input
                              type="text" value={companyForm.legal_representative_id_number}
                              onChange={(e) => setCompanyForm(f => ({ ...f, legal_representative_id_number: e.target.value }))}
                              placeholder="身份证号"
                              className="w-full px-3 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                              disabled={selectedCompanyIsReadonly}
                            />
                          </div>
                          <div>
                            <label className="block text-xs text-gray-500 mb-0.5">统一社会信用代码</label>
                            <input
                              type="text" value={companyForm.unified_social_credit_code}
                              onChange={(e) => setCompanyForm(f => ({ ...f, unified_social_credit_code: e.target.value }))}
                              placeholder="18位代码"
                              className="w-full px-3 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                              disabled={selectedCompanyIsReadonly}
                            />
                          </div>
                          <div className="col-span-2">
                            <label className="block text-xs text-gray-500 mb-0.5">企业地址</label>
                            <input
                              type="text" value={companyForm.address}
                              onChange={(e) => setCompanyForm(f => ({ ...f, address: e.target.value }))}
                              placeholder="详细地址"
                              className="w-full px-3 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                              disabled={selectedCompanyIsReadonly}
                            />
                          </div>
                          <div>
                            <label className="block text-xs text-gray-500 mb-0.5">企业电话</label>
                            <input
                              type="text" value={companyForm.company_phone}
                              onChange={(e) => setCompanyForm(f => ({ ...f, company_phone: e.target.value }))}
                              placeholder="联系电话"
                              className="w-full px-3 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                              disabled={selectedCompanyIsReadonly}
                            />
                          </div>
                          <div>
                            <label className="block text-xs text-gray-500 mb-0.5">开户银行</label>
                            <input
                              type="text" value={companyForm.bank_name}
                              onChange={(e) => setCompanyForm(f => ({ ...f, bank_name: e.target.value }))}
                              placeholder="银行名称"
                              className="w-full px-3 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                              disabled={selectedCompanyIsReadonly}
                            />
                          </div>
                          <div>
                            <label className="block text-xs text-gray-500 mb-0.5">银行账号</label>
                            <input
                              type="text" value={companyForm.bank_account}
                              onChange={(e) => setCompanyForm(f => ({ ...f, bank_account: e.target.value }))}
                              placeholder="账号"
                              className="w-full px-3 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                              disabled={selectedCompanyIsReadonly}
                            />
                          </div>
                          <div>
                            <label className="block text-xs text-gray-500 mb-0.5">传真</label>
                            <input
                              type="text" value={companyForm.fax}
                              onChange={(e) => setCompanyForm(f => ({ ...f, fax: e.target.value }))}
                              className="w-full px-3 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                              disabled={selectedCompanyIsReadonly}
                            />
                          </div>
                          <div>
                            <label className="block text-xs text-gray-500 mb-0.5">邮政编码</label>
                            <input
                              type="text" value={companyForm.postal_code}
                              onChange={(e) => setCompanyForm(f => ({ ...f, postal_code: e.target.value }))}
                              className="w-full px-3 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                              disabled={selectedCompanyIsReadonly}
                            />
                          </div>
                          <div className="col-span-2">
                            <label className="block text-xs text-gray-500 mb-0.5">备注</label>
                            <textarea
                              value={companyForm.notes}
                              onChange={(e) => setCompanyForm(f => ({ ...f, notes: e.target.value }))}
                              rows={2}
                              className="w-full px-3 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                              disabled={selectedCompanyIsReadonly}
                            />
                          </div>
                        </div>
                        {!selectedCompanyIsReadonly && (
                        <div className="flex space-x-2 pt-1">
                          <button
                            onClick={handleCompanySave}
                            disabled={companySaving || !companyForm.name.trim()}
                            className="flex-1 inline-flex justify-center items-center px-3 py-2 text-sm font-medium rounded-lg text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 transition-colors"
                          >
                            {companySaving ? '保存中...' : (selectedCompanyId !== 'new' ? '保存修改' : '创建')}
                          </button>
                          <button
                            onClick={() => {
                              setSelectedCompanyId(null);
                              resetCompanyForm();
                              setCompanySubTab('basic');
                            }}
                            className="px-4 py-2 text-sm font-medium rounded-lg text-gray-600 bg-white border border-gray-300 hover:bg-gray-50 transition-colors"
                          >
                            取消
                          </button>
                        </div>
                        )}
                      </div>
                    )}

                    {/* === 企业财务信息 === */}
                    {companySubTab === 'financial' && typeof selectedCompanyId === 'number' && (
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-gray-500">近三年财务数据</span>
                          {!selectedCompanyIsReadonly && (
                          <button
                            onClick={() => { setShowFiForm(true); setEditingFiId(null); setFiForm({}); }}
                            className="inline-flex items-center px-2 py-1 text-xs font-medium rounded text-white bg-blue-600 hover:bg-blue-700 transition-colors"
                          >
                            <PlusIcon className="w-3 h-3 mr-0.5" />
                            添加年度
                          </button>
                          )}
                        </div>

                        {showFiForm && (
                          <div className="border border-blue-200 bg-blue-50/50 rounded-lg p-3 space-y-2">
                            <div className="text-xs font-medium text-blue-700">
                              {editingFiId ? '编辑财务信息' : '添加财务信息'}
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                              <div>
                                <label className="block text-[10px] text-gray-500 mb-0.5">年度 *</label>
                                <input
                                  type="text" value={fiForm.year || ''}
                                  onChange={e => setFiForm(f => ({ ...f, year: e.target.value }))}
                                  placeholder="如 2023"
                                  className="w-full px-2 py-1 border border-gray-300 rounded text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  disabled={selectedCompanyIsReadonly}
                                />
                              </div>
                              <div>
                                <label className="block text-[10px] text-gray-500 mb-0.5">资产总额（万元）</label>
                                <input
                                  type="text" value={fiForm.total_assets || ''}
                                  onChange={e => setFiForm(f => ({ ...f, total_assets: e.target.value }))}
                                  className="w-full px-2 py-1 border border-gray-300 rounded text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  disabled={selectedCompanyIsReadonly}
                                />
                              </div>
                              <div>
                                <label className="block text-[10px] text-gray-500 mb-0.5">净资产（万元）</label>
                                <input
                                  type="text" value={fiForm.net_assets || ''}
                                  onChange={e => setFiForm(f => ({ ...f, net_assets: e.target.value }))}
                                  className="w-full px-2 py-1 border border-gray-300 rounded text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  disabled={selectedCompanyIsReadonly}
                                />
                              </div>
                              <div>
                                <label className="block text-[10px] text-gray-500 mb-0.5">营业收入（万元）</label>
                                <input
                                  type="text" value={fiForm.revenue || ''}
                                  onChange={e => setFiForm(f => ({ ...f, revenue: e.target.value }))}
                                  className="w-full px-2 py-1 border border-gray-300 rounded text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  disabled={selectedCompanyIsReadonly}
                                />
                              </div>
                              <div>
                                <label className="block text-[10px] text-gray-500 mb-0.5">净利润（万元）</label>
                                <input
                                  type="text" value={fiForm.net_profit || ''}
                                  onChange={e => setFiForm(f => ({ ...f, net_profit: e.target.value }))}
                                  className="w-full px-2 py-1 border border-gray-300 rounded text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  disabled={selectedCompanyIsReadonly}
                                />
                              </div>
                              <div>
                                <label className="block text-[10px] text-gray-500 mb-0.5">研发投入（万元）</label>
                                <input
                                  type="text" value={fiForm.r_and_d_expenses || ''}
                                  onChange={e => setFiForm(f => ({ ...f, r_and_d_expenses: e.target.value }))}
                                  className="w-full px-2 py-1 border border-gray-300 rounded text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  disabled={selectedCompanyIsReadonly}
                                />
                              </div>
                              <div className="col-span-2">
                                <label className="block text-[10px] text-gray-500 mb-0.5">备注</label>
                                <input
                                  type="text" value={fiForm.notes || ''}
                                  onChange={e => setFiForm(f => ({ ...f, notes: e.target.value }))}
                                  className="w-full px-2 py-1 border border-gray-300 rounded text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  disabled={selectedCompanyIsReadonly}
                                />
                              </div>
                            </div>
                            {!selectedCompanyIsReadonly && (
                            <div className="flex space-x-1.5">
                              <button
                                onClick={handleFiSave}
                                disabled={!fiForm.year?.trim()}
                                className="flex-1 inline-flex justify-center items-center px-2.5 py-1.5 text-xs font-medium rounded text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 transition-colors"
                              >
                                保存
                              </button>
                              <button
                                onClick={() => { setShowFiForm(false); setEditingFiId(null); setFiForm({}); }}
                                className="px-3 py-1.5 text-xs font-medium rounded text-gray-600 bg-white border border-gray-300 hover:bg-gray-50 transition-colors"
                              >
                                取消
                              </button>
                            </div>
                            )}
                          </div>
                        )}

                        {fiLoading ? (
                          <div className="text-center py-6 text-xs text-gray-400">加载中...</div>
                        ) : financialInfos.length === 0 && !showFiForm ? (
                          <div className="text-center py-6 text-xs text-gray-400">暂无财务信息</div>
                        ) : (
                          <div className="space-y-1.5">
                            {financialInfos.map(fi => (
                              <div key={fi.id} className="border border-gray-200 rounded-lg p-3 bg-white">
                                <div className="flex items-center justify-between mb-1.5">
                                  <span className="text-sm font-medium text-gray-900">{fi.year} 年度</span>
                                  {!selectedCompanyIsReadonly && (
                                  <div className="flex items-center space-x-1">
                                    <button
                                      onClick={() => {
                                        setEditingFiId(fi.id);
                                        setFiForm(fi);
                                        setShowFiForm(true);
                                      }}
                                      className="p-0.5 rounded text-gray-400 hover:text-blue-500 transition-colors"
                                    >
                                      <PencilSquareIcon className="w-3 h-3" />
                                    </button>
                                    <button
                                      onClick={() => handleFiDelete(fi.id, fi.year)}
                                      className="p-0.5 rounded text-gray-400 hover:text-red-500 transition-colors"
                                    >
                                      <TrashIcon className="w-3 h-3" />
                                    </button>
                                  </div>
                                  )}
                                </div>
                                <div className="grid grid-cols-3 gap-x-4 gap-y-1">
                                  {fi.total_assets && <div className="text-xs text-gray-600">资产总额：<span className="font-medium">{fi.total_assets}万</span></div>}
                                  {fi.net_assets && <div className="text-xs text-gray-600">净资产：<span className="font-medium">{fi.net_assets}万</span></div>}
                                  {fi.revenue && <div className="text-xs text-gray-600">营业收入：<span className="font-medium">{fi.revenue}万</span></div>}
                                  {fi.net_profit && <div className="text-xs text-gray-600">净利润：<span className="font-medium">{fi.net_profit}万</span></div>}
                                  {fi.r_and_d_expenses && <div className="text-xs text-gray-600">研发投入：<span className="font-medium">{fi.r_and_d_expenses}万</span></div>}
                                </div>
                                {fi.notes && <div className="mt-1 text-xs text-gray-400">备注：{fi.notes}</div>}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    {/* === 附件管理（资质/证明/其他） === */}
                    {['qualifications', 'certificates', 'other'].includes(companySubTab) && typeof selectedCompanyId === 'number' && (
                      <div className="space-y-3">
                        {(() => {
                          const catMap: Record<string, { label: string; cat: string }> = {
                            qualifications: { label: '企业资质', cat: 'qualification' },
                            certificates: { label: '相关证明', cat: 'certificate' },
                            other: { label: '其他材料', cat: 'other' },
                          };
                          const cfg = catMap[companySubTab];
                          return (
                            <>
                              <div className="text-xs text-gray-500">{cfg.label} - 文件描述 + 上传文件</div>

                              {/* 文件描述输入框 + 上传按钮 */}
                              {!selectedCompanyIsReadonly && (
                              <div className="flex items-center gap-2">
                                <input
                                  type="text"
                                  value={attDescription}
                                  onChange={(e) => setAttDescription(e.target.value)}
                                  placeholder="文件描述（便于 LLM 理解文件内容）"
                                  className="flex-1 px-2.5 py-1.5 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                                  maxLength={500}
                                />
                                <label className={`inline-flex items-center px-2.5 py-1.5 text-xs font-medium rounded-lg cursor-pointer transition-colors flex-shrink-0 ${
                                  uploadingAtt
                                    ? 'bg-gray-100 text-gray-400'
                                    : 'bg-blue-600 text-white hover:bg-blue-700'
                                }`}>
                                  <input
                                    type="file"
                                    className="hidden"
                                    accept=".jpg,.jpeg,.png,.gif,.webp,.bmp,.pdf,.docx,.doc,.txt,.md,.markdown"
                                    multiple
                                    disabled={uploadingAtt}
                                    onChange={(e) => handleAttUpload(e, cfg.cat)}
                                  />
                                  <PlusIcon className="w-3 h-3 mr-0.5" />
                                  {uploadingAtt ? '上传中...' : '上传文件'}
                                </label>
                              </div>
                              )}

                              {attLoading ? (
                                <div className="text-center py-6 text-xs text-gray-400">加载中...</div>
                              ) : attachments.length === 0 ? (
                                <div className="text-center py-8 text-xs text-gray-400">
                                  <PaperClipIcon className="mx-auto h-8 w-8 text-gray-300 mb-1" />
                                  {selectedCompanyIsReadonly ? '暂无文件' : '暂无文件，请填写文件描述后上传'}
                                </div>
                              ) : (
                                <div className="space-y-1.5">
                                  {attachments.map(att => (
                                    <div key={att.id} className="border border-gray-200 rounded-lg p-2.5 bg-white">
                                      <div className="flex items-start justify-between">
                                        <div className="flex-1 min-w-0">
                                          <div className="flex items-center gap-1.5">
                                            <PaperClipIcon className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                                            <span className="text-xs font-medium text-gray-700 truncate">{att.file_name}</span>
                                          </div>
                                          <span className="text-[10px] text-gray-400 ml-5">
                                            {att.file_type.toUpperCase()} · {att.file_size > 1024 ? `${(att.file_size / 1024).toFixed(1)} KB` : `${att.file_size} B`}
                                            {att.extracted_text_length > 0 && (
                                              <span className={`ml-2 inline-flex items-center gap-0.5 ${
                                                att.extracted_text_length > companyAttTextLimit
                                                  ? 'text-red-500 font-medium'
                                                  : 'text-green-500'
                                              }`}>
                                                {att.extracted_text_length > companyAttTextLimit ? (
                                                  <ExclamationCircleIcon className="w-3 h-3" />
                                                ) : (
                                                  <CheckCircleIcon className="w-3 h-3" />
                                                )}
                                                提取 {att.extracted_text_length} 字符
                                                {att.extracted_text_length > companyAttTextLimit && (
                                                  <span className="text-[10px] text-red-400">（超限，仅前{companyAttTextLimit}字符送入LLM）</span>
                                                )}
                                              </span>
                                            )}
                                          </span>
                                          {/* 文件描述：可编辑 */}
                                          <div className="mt-1 ml-5">
                                            {editingAttDescId === att.id ? (
                                              <div className="flex items-center gap-1">
                                                <input
                                                  type="text"
                                                  value={editingAttDescValue}
                                                  onChange={(e) => setEditingAttDescValue(e.target.value)}
                                                  onKeyDown={(e) => { if (e.key === 'Enter') handleAttDescriptionSave(att.id); if (e.key === 'Escape') setEditingAttDescId(null); }}
                                                  className="flex-1 px-2 py-1 text-xs border border-blue-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                                                  maxLength={500}
                                                  autoFocus
                                                />
                                                <button
                                                  onClick={() => handleAttDescriptionSave(att.id)}
                                                  className="px-1.5 py-0.5 text-[10px] bg-blue-600 text-white rounded hover:bg-blue-700"
                                                >
                                                  保存
                                                </button>
                                                <button
                                                  onClick={() => setEditingAttDescId(null)}
                                                  className="px-1.5 py-0.5 text-[10px] text-gray-500 hover:text-gray-700"
                                                >
                                                  取消
                                                </button>
                                              </div>
                                            ) : att.description ? (
                                              <div className="flex items-center gap-1 group">
                                                <span className="text-xs text-gray-600 truncate">描述：{att.description}</span>
                                                {!selectedCompanyIsReadonly && (
                                                <button
                                                  onClick={() => { setEditingAttDescId(att.id); setEditingAttDescValue(att.description || ''); }}
                                                  className="p-0.5 rounded text-gray-400 hover:text-blue-500 opacity-0 group-hover:opacity-100 transition-opacity"
                                                >
                                                  <PencilSquareIcon className="w-3 h-3" />
                                                </button>
                                                )}
                                              </div>
                                            ) : !selectedCompanyIsReadonly ? (
                                              <button
                                                onClick={() => { setEditingAttDescId(att.id); setEditingAttDescValue(''); }}
                                                className="text-xs text-gray-400 hover:text-blue-500 transition-colors"
                                              >
                                                + 添加文件描述
                                              </button>
                                            ) : null}
                                          </div>
                                        </div>
                                        <div className="flex items-center gap-0.5 flex-shrink-0 mt-0.5">
                                          {IMAGE_FILE_TYPES.includes(att.file_type) && att.file_url ? (
                                            <button
                                              onClick={() => setCompanyAttPreviewUrl(API_BASE_URL + att.file_url)}
                                              className="p-1 rounded text-gray-400 hover:text-blue-500 hover:bg-blue-50 transition-colors"
                                              title="预览图片"
                                            >
                                              <EyeIcon className="w-3.5 h-3.5" />
                                            </button>
                                          ) : !selectedCompanyIsReadonly ? (
                                            <button
                                              onClick={() => handleCompanyAttEditText(att)}
                                              className="p-1 rounded text-gray-400 hover:text-green-500 hover:bg-green-50 transition-colors"
                                              title="编辑内容"
                                            >
                                              <PencilSquareIcon className="w-3.5 h-3.5" />
                                            </button>
                                          ) : null}
                                          {!selectedCompanyIsReadonly && (
                                          <button
                                            onClick={() => handleAttDelete(att.id, att.file_name)}
                                            className="p-1 rounded text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                                          >
                                            <TrashIcon className="w-3.5 h-3.5" />
                                          </button>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </>
                          );
                        })()}
                      </div>
                    )}
                  </div>
                  );
                })()
                ) : (
                  <div className="flex-1 flex flex-col items-center justify-center h-full text-center py-12">
                    <BuildingOffice2Icon className="h-12 w-12 text-gray-200 mb-3" />
                    <p className="text-sm text-gray-400">请从左侧选择企业进行管理</p>
                    <p className="text-xs text-gray-300 mt-1">或点击"新增"按钮创建企业</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ===== 提词管理 ===== */}
          {activeTab === 'prompt' && (
            <div className="space-y-6">
              <div className="text-sm text-gray-600">
                控制生成技术标/商务标章节内容时，向 AI 传入哪些上下文信息。取消勾选的项将不会出现在提示词中。
              </div>

              {promptConfigMsg && (
                <div className={`px-4 py-2.5 rounded-lg text-sm ${
                  promptConfigMsg.type === 'success'
                    ? 'bg-green-50 text-green-700 border border-green-200'
                    : 'bg-red-50 text-red-700 border border-red-200'
                }`}>
                  {promptConfigMsg.text}
                </div>
              )}

              <div className="grid grid-cols-2 gap-6">
                {/* 技术部分 */}
                <div className="border border-gray-200 rounded-lg p-4">
                  <h3 className="text-sm font-semibold text-gray-800 mb-3 pb-2 border-b border-gray-100">
                    技术部分上下文
                  </h3>
                  {promptConfigLoading ? (
                    <div className="flex items-center justify-center py-6">
                      <div className="w-4 h-4 border-2 border-blue-400 border-t-transparent rounded-full animate-spin mr-2" />
                      <span className="text-sm text-gray-400">加载中...</span>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {PROMPT_CONTEXT_CATEGORIES.map(cat => (
                        <div key={`tech-${cat.category}`}>
                          <div className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-1.5 ml-1">
                            {cat.category}
                          </div>
                          <div className="space-y-0.5">
                            {cat.items.map(item => (
                              <label
                                key={`tech-${item.key}`}
                                className="flex items-center gap-2.5 px-2 py-1.5 rounded-md hover:bg-gray-50 cursor-pointer transition-colors"
                              >
                                <input
                                  type="checkbox"
                                  checked={promptContextTech.has(item.key)}
                                  onChange={() => togglePromptContext('tech', item.key)}
                                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                />
                                <span className="text-sm text-gray-700 select-none">{item.label}</span>
                              </label>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* 商务部分 */}
                <div className="border border-gray-200 rounded-lg p-4">
                  <h3 className="text-sm font-semibold text-gray-800 mb-3 pb-2 border-b border-gray-100">
                    商务部分上下文
                  </h3>
                  {promptConfigLoading ? (
                    <div className="flex items-center justify-center py-6">
                      <div className="w-4 h-4 border-2 border-blue-400 border-t-transparent rounded-full animate-spin mr-2" />
                      <span className="text-sm text-gray-400">加载中...</span>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {PROMPT_CONTEXT_CATEGORIES.map(cat => (
                        <div key={`biz-${cat.category}`}>
                          <div className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-1.5 ml-1">
                            {cat.category}
                          </div>
                          <div className="space-y-0.5">
                            {cat.items.map(item => (
                              <label
                                key={`biz-${item.key}`}
                                className="flex items-center gap-2.5 px-2 py-1.5 rounded-md hover:bg-gray-50 cursor-pointer transition-colors"
                              >
                                <input
                                  type="checkbox"
                                  checked={promptContextBiz.has(item.key)}
                                  onChange={() => togglePromptContext('biz', item.key)}
                                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                />
                                <span className="text-sm text-gray-700 select-none">{item.label}</span>
                              </label>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* 保存按钮 */}
              <div className="flex justify-end">
                <button
                  onClick={savePromptConfig}
                  disabled={promptConfigLoading}
                  className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors"
                >
                  {promptConfigLoading ? (
                    <>
                      <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin mr-1.5" />
                      保存中...
                    </>
                  ) : (
                    '保存设置'
                  )}
                </button>
              </div>
            </div>
          )}

          {/* ===== 回收站 ===== */}
          {activeTab === 'trash' && (
            <div className="space-y-4">
              <div className="text-sm text-gray-600">
                查看已删除的项目，可以恢复或彻底删除。
              </div>

              {trashMsg && (
                <div className={`px-4 py-2.5 rounded-lg text-sm ${
                  trashMsg.type === 'success'
                    ? 'bg-green-50 text-green-700 border border-green-200'
                    : 'bg-red-50 text-red-700 border border-red-200'
                }`}>
                  {trashMsg.text}
                </div>
              )}

              {trashLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="w-5 h-5 border-2 border-blue-400 border-t-transparent rounded-full animate-spin mr-2" />
                  <span className="text-sm text-gray-500">加载中...</span>
                </div>
              ) : trashItems.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-gray-400">
                  <TrashIcon className="w-10 h-10 mb-3 opacity-30" />
                  <p className="text-sm">回收站为空</p>
                  <p className="text-xs mt-1">删除的项目会出现在这里</p>
                </div>
              ) : (
                <>
                  <div className="border border-gray-200 rounded-lg overflow-hidden">
                    {/* 表头 */}
                    <div className="grid grid-cols-[1fr_120px_80px_80px] gap-2 px-4 py-2.5 bg-gray-50 border-b border-gray-200 text-xs font-medium text-gray-500">
                      <span>项目名称</span>
                      <span>删除时间</span>
                      <span className="text-center">恢复</span>
                      <span className="text-center">清空</span>
                    </div>
                    {/* 列表 */}
                    {trashItems.map(item => (
                      <div
                        key={item.id}
                        className="grid grid-cols-[1fr_120px_80px_80px] gap-2 px-4 py-3 border-b border-gray-100 last:border-b-0 items-center hover:bg-gray-50/50 transition-colors"
                      >
                        <div className="min-w-0">
                          <div className="text-sm text-gray-900 truncate">{item.name}</div>
                          {item.description && (
                            <div className="text-xs text-gray-400 truncate mt-0.5">{item.description}</div>
                          )}
                        </div>
                        <span className="text-xs text-gray-400">{formatDeletedTime(item.deleted_at)}</span>
                        <button
                          onClick={() => handleRestore(item.id)}
                          className="flex items-center justify-center px-2 py-1 text-xs font-medium text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                          title="恢复项目"
                        >
                          <ArrowUturnLeftIcon className="w-3.5 h-3.5 mr-1" />
                          恢复
                        </button>
                        <button
                          onClick={() => handlePermanentDelete(item.id, item.name)}
                          className="flex items-center justify-center px-2 py-1 text-xs font-medium text-red-600 hover:bg-red-50 rounded-md transition-colors"
                          title="彻底删除"
                        >
                          <TrashIcon className="w-3.5 h-3.5 mr-1" />
                          清空
                        </button>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-gray-400 text-right">
                    共 {trashItems.length} 个已删除项目
                  </p>
                </>
              )}
            </div>
          )}
        </div>

        {/* 底部按钮 */}
        <div className="flex items-center justify-end space-x-3 px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-xl">
          <button
            onClick={onClose}
            className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-blue-500 transition-colors"
          >
            关闭
          </button>
        </div>
      </div>

      {/* 企业附件图片预览弹窗 */}
      {companyAttPreviewUrl && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 cursor-pointer"
          onClick={() => setCompanyAttPreviewUrl(null)}
        >
          <button
            className="absolute top-4 right-4 p-2 rounded-full bg-white/20 text-white hover:bg-white/40 transition-colors"
            onClick={(e) => { e.stopPropagation(); setCompanyAttPreviewUrl(null); }}
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
          <img
            src={companyAttPreviewUrl}
            alt="预览"
            className="max-w-[90vw] max-h-[90vh] object-contain rounded-lg shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      {/* 企业附件提取文本编辑器 */}
      <ExtractedTextEditor
        open={!!editingCompanyAttText && !editCompanyAttLoading}
        title={editingCompanyAttText?.file_name || ''}
        value={editCompanyAttContent}
        onChange={setEditCompanyAttContent}
        resolveUrl={resolveImageUrl}
        onSave={handleCompanyAttSaveText}
        onClose={() => { setEditingCompanyAttText(null); setEditCompanyAttContent(''); }}
      />

      {/* 共享弹窗 */}
      {shareTarget && (
        <ShareDialog
          isOpen={true}
          onClose={() => setShareTarget(null)}
          entityType={shareTarget.type}
          entityId={shareTarget.id}
          entityName={shareTarget.name}
          isOwner={shareTarget.isOwner}
        />
      )}

      {/* 转移所有权弹窗 */}
      {transferTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={closeTransferDialog} />
          <div className="relative bg-white rounded-xl shadow-xl w-full max-w-sm mx-4">
            {/* 标题栏 */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">转移所有权</h2>
                <p className="text-xs text-gray-500 mt-0.5">
                  {transferTarget.type === 'knowledge_base' ? '知识库' : '企业'}：{transferTarget.name}
                </p>
              </div>
              <button
                onClick={closeTransferDialog}
                className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>

            {/* 内容 */}
            <div className="px-5 py-4 space-y-4">
              <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs text-amber-700">
                转移后该实体将归属目标用户。已有的分享记录默认保留并自动归属新所有者（不会影响其他项目引用），也可选择取消所有分享。
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">目标用户名</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={transferUsername}
                    onChange={(e) => {
                      setTransferUsername(e.target.value);
                      setTransferCheckResult(null);
                    }}
                    placeholder="输入要转移给的用户的用户名"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <button
                    onClick={handleCheckUser}
                    disabled={transferChecking || !transferUsername.trim()}
                    className="inline-flex items-center gap-1 px-3 py-2 text-xs font-medium rounded-lg border transition-colors disabled:opacity-50 disabled:cursor-not-allowed bg-blue-50 border-blue-300 text-blue-700 hover:bg-blue-100"
                  >
                    {transferChecking ? (
                      <svg className="w-3.5 h-3.5 animate-spin" viewBox="0 0 24 24" fill="none">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                    ) : (
                      <CheckCircleIcon className="w-3.5 h-3.5" />
                    )}
                    检查
                  </button>
                </div>
                {transferCheckResult?.exists && (
                  <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                    <CheckCircleIcon className="w-3.5 h-3.5" />
                    用户存在：{transferCheckResult.display_name || transferUsername.trim()}
                  </p>
                )}
                {transferCheckResult && !transferCheckResult.exists && (
                  <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                    <ExclamationCircleIcon className="w-3.5 h-3.5" />
                    用户不存在
                  </p>
                )}
              </div>

              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={transferCancelShares}
                  onChange={(e) => setTransferCancelShares(e.target.checked)}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="text-sm text-gray-600">同时取消所有已分享记录</span>
              </label>
            </div>

            {/* 底部按钮 */}
            <div className="flex items-center justify-end space-x-3 px-5 py-4 border-t border-gray-200 bg-gray-50 rounded-b-xl">
              <button
                onClick={closeTransferDialog}
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 transition-colors"
              >
                取消
              </button>
              <button
                onClick={handleTransfer}
                disabled={transferring || !transferCheckResult?.exists}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-orange-600 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-orange-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <ArrowPathIcon className="w-4 h-4 mr-1" />
                {transferring ? '转移中...' : '确认转移'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
      {showPoster && (
        <ReferralPoster
          open={showPoster}
          link={`${window.location.origin}/login?ref=${referralCode}`}
          onClose={() => setShowPoster(false)}
          slogans={posterSlogans ?? undefined}
        />
      )}
    </>
  );
};

export default SettingsDialog;
